import {
  Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo,
  useRef, useState,
  type CSSProperties, type ForwardedRef, type ReactNode,
} from 'react'
import {
  BookOpen, Link as LinkIcon, MessageSquare, Mic, Plus, SquarePen,
} from 'lucide-react'
import type { Card } from '../types'
import type { DeckRowRegistry } from '../utils/deckRowRegistry'
import { useLocale } from '../i18n/context'
import { findTermSide } from '../utils/cardSides'
import { displaySides, fieldTypeLabel, sideKindLabel } from '../utils/cardSideDisplay'
import { listRow, type ListRowExtra, type ExtraGlyphKind } from '../utils/deckListRow'
import {
  TERM_FIELD_TYPE, findFieldKeyOfType, stepTarget, type FieldType,
} from '../utils/deckCardNavigation'
import { useLookupPaletteStore } from '../stores/lookupPaletteStore'
import { shortcutLabel } from '../utils/keyboardHints'
import { resolveDeckEnterRung } from '../utils/deckEnterLadder'
import { DECK_FLOOR_INSET } from '../utils/deckPaneViewport'
import ActiveVocabCard, { type ActiveVocabCardHandle } from './shared/ActiveVocabCard'
import FieldTypeChooser from './FieldTypeChooser'
import { TERM_FIELD_KEY } from './shared/VocabCard'
import MasteryMeter from './shared/MasteryMeter'
import { ForgotWordButton } from './shared/MasteryResetConfirm'
import UnsavedMark from './shared/UnsavedMark'
// The pane geometry, shared with the deck's text panels (handoff §19.6).
import { leftColumnStyle, leftPanelStyle, panelsShellStyle, rightPanelStyle } from './deckPaneLayout'
import { useCardWriteFailed, useVisibleCard } from '../hooks/useCardDisplay'

/** The active card in the right panel while an unsaved new card (⌘⏎, brief
 * §22) is showing — never a real card id, so it can never collide with one. */
const DRAFT_CARD_ID = '__draft__'

/**
 * The empty right panel's two keys (design pass, 2026-09-09) — deliberately
 * NOT the full keyboard cycle this used to list (⌘⏎/⇥/⇧⇥/⎋/⌘⇧F/⌥⌫/⌘F/⌘K):
 * that list belongs to the editing state it describes, and showing all seven
 * combos before a card is even open crowded a state whose job is just to
 * get the reader TO a card. These two are what "nothing selected" can
 * actually act on — opening the first card (wired below, alongside the
 * matching "Enter to edit" once a card is open) and searching the deck
 * (`onSearchInDeck`, already global). `shortcutLabel` (from
 * `utils/keyboardHints`) renders each in the reader's OWN platform's glyphs.
 */
const EMPTY_PANEL_HINTS: ReadonlyArray<{ combo: string; labelKey: 'kbdOpenFirstCard' | 'kbdSearchDeck' }> = [
  { combo: shortcutLabel(['Enter']), labelKey: 'kbdOpenFirstCard' },
  { combo: shortcutLabel(['mod', 'F']), labelKey: 'kbdSearchDeck' },
]

/**
 * The desktop deck editor: a card list on the left, the selected card's fields
 * on the right (brief §22, implementation item 8).
 *
 * Three properties are decisions rather than layout, and each is the reason a
 * simpler arrangement was not built:
 *
 *  • THE RIGHT PANEL READS BY DEFAULT AND EDITS ON REQUEST (ADR-067 amendment,
 *    2026-09-08). This used to say "there are no modes": the mouse does not
 *    miss, so the surface was always editable. What that reasoning missed is
 *    that the mobile split never bought only mistap protection — it also
 *    bought a state in which a card can simply be READ, and a reader who opens
 *    a deck is at least as often checking what is in it as correcting it. So a
 *    click opens a card for reading (no field frames, no handles, no delete
 *    controls, no labels, nothing truncated), and editing is entered
 *    explicitly: per card via the panel's own `Edit`, or per deck via the
 *    deck-level mode (`deckEditMode`, owned by the page). A click must never
 *    both select a card and place the caret in the field under the pointer.
 *    The two states have two widths — see `rightPanelWidth` below — and the
 *    move between them is one eased motion, not a layout jump.
 *
 *  • TWO SCROLLERS, NOT NESTED SCROLLING. §3 principle 3 forbids a scrolling
 *    container inside a scrolling page — and then permits exactly this pair,
 *    because neither panel is inside the other. The prohibition that does carry
 *    over is the one on scrolling INSIDE a text field: the right panel's fields
 *    grow to their content and never scroll internally, which is `GrowingField`
 *    in `ActiveVocabCard` and is why that component is reused rather than
 *    reimplemented.
 *
 *  • THE RIGHT PANEL IS `ActiveVocabCard`, THE SAME COMPONENT THE PHONE USES.
 *    §22 names the reuse, and the reason is not economy: field rows, the
 *    handle/label/trash band, Term's empty gutter, the growth rule, `+ Field`,
 *    Fill, the ADR-066 authority report and the STUDIED badge are all rules
 *    about a card, not about a viewport. A second implementation would be a
 *    second place for every one of them to drift.
 *
 * What this pass deliberately does NOT build, each being its own step: desktop
 * multi-select's ⇧/⌘-click range semantics and set-summary panel. The existing
 * checkbox multi-select is carried over unchanged so nothing that worked
 * stops working.
 *
 * `narrow` (design brief §4h) and pane-level paste recognition (§4d) were
 * added in a later pass — item 8's second half. Below the 1040px threshold
 * the right panel becomes a 480px sheet over the list rather than shrinking
 * the two panes past what their content needs; see `computedLeftPanelStyle`
 * / `computedRightPanelStyle` below for why this is CSS on the same JSX
 * tree, never a structural fork. Pasting 2+ lines anywhere on the pane is
 * offered as a bulk add and hands off to the existing text-import flow
 * (`onPasteWordList`) rather than parsing lines into cards here — see that
 * prop's own comment.
 *
 * **The keyboard cycle (⌘↵/⇥/⇧⇥/⎋/⌘⇧F/⌥⌫/⌘F/⌘K, brief §22).** ⌥⌫ and ⌘⇧F act
 * on exactly one card and are handled entirely inside `ActiveVocabCard`. What
 * lives here is everything that crosses a card boundary — ⇥ past the last
 * field, ⇧⇥ before Term, and ⌘⏎ — because only this component knows what
 * "next card" means. It reuses the exact field-type-carrying walk brief §13
 * built for the mobile Previous/Next arrows (`walkTypeRef`, `goToCard`,
 * `findFieldKeyOfType`): landing on a card that lacks the carried type falls
 * back to Term and offers `+ Add <Type>` in its place, the same affordance
 * `DeckMobileFeed` renders. Desktop has no arrow-key equivalent — §22 says
 * modifier+arrow combinations are not used because they collide with the
 * browser and window manager — so ⇥/⇧⇥ at a card's ends are the ONLY way a
 * keyboard crosses cards here.
 */

interface Props {
  deckId: string
  deckLanguage: string
  /** The rows to list: the loaded window, or the server's matches while a
   * search is running. Never a blend — see `DeckDetailPage`. */
  cards: Card[]
  /** Card → its position in THIS LIST (0-based): the loaded window's index map,
   * or a search match's server-stated deck index. What the row logic runs on —
   * shift-range selection, insert dividers, `⇥` between cards. It is not a card
   * number: under an anchor the window starts partway into the deck, and this
   * counts from the start of the window. */
  indexOf: (card: Card) => number
  /** Card → its number in the DECK (1-based, ready to print). Separate from
   * `indexOf` because printing that one numbered an anchored window's rows
   * 1–30, so a deck opened at page 5 read as though it were showing its
   * beginning. The phone feed has always numbered its rows this way. */
  numberOf: (card: Card) => number
  /** The deck number of the window's FIRST row (1-based). Only the unsaved
   * draft needs it: it has no id to look up, and the position it carries is a
   * window index from the divider it was opened at. */
  windowStartsAtCard: number
  /** The deck's true size — the same value `DeckMobileFeed` takes, and for the
   * same reason: a card `⌘⏎`-created below the loaded window must not wear a
   * number computed from how much of the deck happens to be paged in. */
  deckCardCount: number
  isOwner: boolean
  /** True while an in-deck search is open: the insert dividers are withheld,
   * because a position inside a result set says nothing about position among
   * the cards it is not showing. */
  searching: boolean
  /** A card created from an insert divider (the hairline-with-a-plus between
   * rows, §22) has been POSTed, and the server appended it at the end of the
   * deck. The caller moves it to `index` — it owns the deck's own card array
   * and the move mutation, and this component only knows which divider was
   * pressed.
   *
   * The divider is an entry point, not an editor (ADR-067 ninth amendment):
   * what it opens is the right panel's own draft, the same surface `⌘⏎` opens,
   * carrying a position. Nothing opens over the panel. */
  onCardInsertedAt: (card: Card, index: number) => void
  onSaveError: (message: string) => void
  /** A field deletion that removed content — the caller shows the transient
   * Undo (§12). */
  onFieldDeleted: (info: { label: string; restore: () => void }) => void
  /** A reorder handed a repeated kind's authority to another field (ADR-066
   * Decision 1). The caller words the report. */
  onFieldOrderChanged: (labels: string[]) => void
  /** A different card was opened on the right. The caller drops any field-level
   * transient it is showing: a field Undo is a closure into the draft of the
   * card that is going away, and offering it over a different card would put it
   * back somewhere it never was. */
  onSelectionChanged: () => void
  /** ⌘F (brief §22) — the caret is inside a field, so this focuses the
   * page's own in-deck search box rather than trying to own one here. */
  onSearchInDeck: () => void
  /** Something the PAGE layers over this screen is open — the deck menu, a
   * confirm, the Reorder screen, the word reference panel. The Enter ladder's
   * rung 1 has to know about those as well as about this component's own two,
   * or Enter pressed with focus on the page underneath one of them opens an
   * edit session on a card nobody can see. */
  pageOverlayOpen: boolean
  // ── Per-row overflow actions, kept from the list this replaces ────────────
  onMoveCardToDeck: (cardId: string) => void
  onDeleteCard: (cardId: string) => void
  /**
   * The word reference panel, which is about the card being read. Opened from
   * the reading pane (not the row face — DESIGN_SYSTEM.md §12 — and not the
   * column head, which is about the list rather than any one card), and it
   * follows the selection while it is open: `referenceCardId` is the card it is
   * showing, or null when it is closed or showing a free term (a synonym the
   * reader clicked inside it). `onShowReference` sets, never toggles.
   */
  referenceCardId: string | null
  onShowReference: (cardId: string, term: string) => void
  onCloseReference: () => void
  /** The panel itself, built by the page (it owns the panel's state), or null
   * while it is closed. Laid over the reading pane's own box — see
   * `referenceLayerStyle`. */
  referencePanel: ReactNode | null
  /** A `?card=` deep link's target — tinted so the reader can see which row the
   * link meant, as the previous list did. */
  highlightedCardId: string | null
  /**
   * Where this render publishes its row elements, keyed by card id.
   *
   * The deep-link motion lives on the page (it owns the anchor and the index
   * the target resolves to) but the elements are made here, so they are handed
   * over rather than looked up: ADR-067 Decision 6 forbids scroll-to-card by
   * node lookup, and this is the same shape by which the phone's focus
   * placement receives its row and card. Registration rides on the `rowRef`
   * the ⎋ focus-return already uses — one write per row, two readers, so the
   * two can never disagree about which element a card is.
   *
   * Registering is also how the owner of that arrival LEARNS the row exists.
   * The arrival effect is a component above this one, so the commit that first
   * renders these rows does not re-run it; the registry calls back instead of
   * leaving it to wait for a render that may never come.
   *
   * The LEFT PANEL is registered too, as the element that scrolls those rows.
   * On this layout the list is its own scrollport — the permitted pair of §22,
   * not a scrollport inside a scrollport — so moving the page moves the wrong
   * thing: the page's own range here is the height of the chrome above this
   * shell, after which the shell is anchored to the viewport and the page has
   * nowhere left to go, while a row part-way down a 30-card page is well past
   * it.
   * The arrival cannot know that from where it lives, and must not go looking;
   * the layout that made both the rows and the box they scroll in says so.
   */
  rowRegistry?: DeckRowRegistry
  // ── Multi-select, carried over verbatim from the previous list ────────────
  isAnySelected: boolean
  isSelected: (cardId: string) => boolean
  onCheckboxClick: (e: React.MouseEvent, cardId: string, index: number) => void
  onCheckboxPointerDown: (e: React.PointerEvent, cardId: string) => void
  onCheckboxPointerEnter: (cardId: string) => void
  /** Paging sentinel for whichever list is showing. */
  loadMoreRef: React.RefObject<HTMLDivElement | null>
  isFetchingNextPage: boolean
  /** Below the 1040px two-pane threshold (design brief §4h): the list keeps
   * the full width and the selected card opens as a 480px sheet over it,
   * rather than the two panes shrinking past what their content needs. */
  narrow: boolean
  /** A paste of two or more lines was caught on the pane, outside any field
   * (brief §4d). The caller hands off to the existing text-import flow with
   * the pasted text pre-loaded — this component never parses the lines into
   * cards itself. */
  onPasteWordList: (lines: string[]) => void
  /**
   * The deck-level editing mode (ADR-067 amendment, 2026-09-08): every card
   * opens straight into editing while it is on.
   *
   * Owned by the page, not by this component, because the mode belongs to the
   * DECK rather than to a card — its indicator and its off switch live in the
   * screen-header bar beside the deck's name, which is above this component
   * in the tree and is reachable while no card is selected at all. What this
   * component owns is the single-card case (`editingCardId` below); the two
   * are combined but never confused, which is what makes a card panel's own
   * `Done` leave the deck mode alone.
   *
   * Always `false` for a deck the reader cannot edit — a system deck has no
   * editing entry and no deck mode, and reading is its only state.
   */
  deckEditMode: boolean
  /** Bumped by the page's own `+ Card` control, which sits above this
   * component in the tree (beside the search box) and so cannot reach the
   * draft directly.
   *
   * A counter rather than a boolean: pressing `+ Card` twice in a row is two
   * requests for a new card, and a flag would swallow the second. It is the
   * same shape `Selected.nonce` uses a few lines down, for the same reason.
   * `0` means "never pressed" and starts nothing, so an ordinary mount does
   * not open a draft nobody asked for. */
  newCardNonce: number
  /**
   * The list column's own head — the card-search field, the shown-count and
   * `Add card`, plus whatever narrowing chrome is currently up (the filter
   * chips, the selection toolbar, the anchor notice).
   *
   * It is passed in rather than rendered here because every control in it is
   * already the page's: the search box is the page's query state and the
   * target of ⌘F, `Add card` bumps `newCardNonce` above, and the counts come
   * from the page's own server queries. What this component supplies is the
   * one thing the page cannot: the head's POSITION — inside the list column,
   * at exactly the rows' own width, above the scroll region rather than in it
   * (DESIGN_SYSTEM.md §13, "Scope decides width").
   *
   * It sits outside the scroller, so it does not scroll and is not a second
   * scrolling region: the screen has exactly two, the rows and the pane's
   * body.
   */
  listHead?: ReactNode
  /**
   * What the list's scroll region shows INSTEAD of rows — a search still
   * running, a search that failed, a query or a chip that matched nothing.
   *
   * These used to replace the whole two-pane shell on the page, which took the
   * search field away with them: typing a word that matches no card removed
   * the box the word had just been typed into. Rendered here, the column head
   * and the pane both stay where they are and only the rows are replaced.
   */
  listPlaceholder?: ReactNode
  /**
   * What is currently narrowing the rows, if anything — the in-deck search
   * with its query, or the chip filter.
   *
   * The panel needs this for one thing only: when the selected card is not
   * among the rows, it is the difference between "the card is hidden and here
   * is what to lift" and "the card is gone". Both look identical from inside
   * this component — the card simply is not in `cards` — and only the page
   * knows which. It is not a second notion of what the list contains: the rows
   * are still whatever `cards` holds, and nothing here re-filters them.
   */
  listNarrowing?: { kind: 'search'; query: string } | { kind: 'filter' } | null
  /**
   * The deck's description, shown in the pane's resting state (nothing
   * selected). It lived in the band above the columns, clamped at two lines;
   * the band moved into the screen-header bar (ADR-067, thirteenth
   * amendment), which has no room for a paragraph, and the resting pane is
   * the deck-level moment on this screen with room to spare. Shown whole.
   */
  deckDescription?: string | null
}

/**
 * What the deck screen's Escape ladder (`utils/deckEscapeLadder`) needs from
 * this component, which owns two of the ladder's five rungs.
 *
 * A ref handle rather than state lifted to the page: both rungs are already
 * this component's own state (`editingCardId`/`draft`, and `selected`), and
 * the page needs to ASK about them and ACT on them at one instant inside a
 * key handler — not to re-render on every change of them. Lifting them would
 * put a card-level concern in the page and make every selection change a page
 * render. Same shape, and the same reasoning, as `ActiveVocabCardHandle`.
 */
export interface DeckDesktopPanelsHandle {
  /** Rung 2 — is a card in a PER-CARD edit session, i.e. one this component
   *  can end on its own? False while deck mode is what makes the card
   *  editable, because then there is nothing per-card to end. */
  isEditSessionOpen: () => boolean
  /** Rung 2's action — end the edit session, leaving the card selected and
   *  open for reading. Never clears the selection (the draft excepted; see
   *  `endEditSession` below). Returns whether it acted. */
  endEditSession: () => boolean
  /** Rung 5 — is a card selected at all? */
  hasSelection: () => boolean
  /** Rung 5's action — return the pane to its resting empty state. */
  clearSelection: () => void
}

/** What the right panel is currently opened on. `fieldKey` is the field the
 * caret was asked to land in — `TERM_FIELD_KEY`, a side's id, or null for
 * "wherever it already is". */
interface Selected {
  cardId: string
  fieldKey: string | null
  /** Bumped on every click so that clicking the SAME field twice re-places the
   * caret rather than being swallowed as "no change". */
  nonce: number
  /** Set when a card-boundary walk (⇥/⇧⇥) landed here holding a type this
   * card does not have (brief §13, carried over from the mobile arrows). */
  missingType?: FieldType
}

function DeckDesktopPanelsImpl({
  deckId, deckLanguage, cards, indexOf, numberOf, windowStartsAtCard, deckCardCount, isOwner, searching,
  onCardInsertedAt, onSaveError, onFieldDeleted, onFieldOrderChanged,
  onSelectionChanged, onSearchInDeck, pageOverlayOpen, onMoveCardToDeck, onDeleteCard,
  referenceCardId, onShowReference, onCloseReference, referencePanel, highlightedCardId,
  rowRegistry,
  isAnySelected, isSelected, onCheckboxClick, onCheckboxPointerDown, onCheckboxPointerEnter,
  loadMoreRef, isFetchingNextPage, narrow, onPasteWordList, newCardNonce, deckEditMode,
  listHead, listPlaceholder, listNarrowing, deckDescription,
}: Props, ref: ForwardedRef<DeckDesktopPanelsHandle>) {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  const ce = tr.cardEditor
  const openSearchEverywhere = useLookupPaletteStore((s) => s.openAsModal)

  const [selected, setSelected] = useState<Selected | null>(null)
  // The one card put into editing by an explicit action on the panel — the
  // single-card half of the two ways in (the other is `deckEditMode`, which
  // belongs to the deck and is owned by the page). Holds `selected.cardId`,
  // not `card.id`, so the unsaved draft (whose card id is `''` for its whole
  // life) is distinguishable from a real card.
  const [editingCardId, setEditingCardId] = useState<string | null>(null)
  const [fieldChooserOpen, setFieldChooserOpen] = useState(false)
  const activeCardRef = useRef<ActiveVocabCardHandle>(null)
  // `onMoveCardToDeck` / `onDeleteCard` were reached through the row's `···`
  // overflow, which §12.6 forbids on the row face. They get no new home: the
  // selection bar carries Move to, Copy to and Delete for a selection of one.
  void onMoveCardToDeck; void onDeleteCard

  const rowElsRef = useRef<Map<string, HTMLDivElement>>(new Map())
  // ⎋'s fallback focus target (below) when the card being left is the unsaved
  // draft — it has no row of its own to hand focus back to. It is also the
  // element that scrolls the rows on this layout, which is why the same node
  // goes to the row registry: see `rowRegistry` in the props above.
  const leftPanelRef = useRef<HTMLDivElement | null>(null)
  const [leftPanelEl, setLeftPanelEl] = useState<HTMLDivElement | null>(null)
  const setLeftPanel = useCallback((el: HTMLDivElement | null) => {
    leftPanelRef.current = el
    setLeftPanelEl(el)
    // Last, and with the element in place: the reader of this is the deep-link
    // arrival, which measures what it is handed.
    rowRegistry?.setScrollport(el)
  }, [rowRegistry])
  /**
   * The list scroller's right-hand chrome, in px: its 1px rule plus whatever
   * the platform's scrollbar currently takes out of the content box.
   *
   * The column head sits ABOVE the scroller rather than inside it (see the
   * `listHead` prop), so it does not lose that width by itself — and the head
   * is the one thing on this screen that claims to be "at exactly the list's
   * width" (§13). Without this inset the search field's right edge stands a
   * scrollbar past every row's, which is the misregistration the claim exists
   * to rule out.
   *
   * Measured, not written down, for the same reason `useScrollportMetrics`
   * measures the scrollport: the number is 0 where scrollbars overlay and
   * ~15 where they take space, it changes the moment the list becomes long
   * enough to scroll, and no constant is right in both cases.
   */
  const [listScrollChrome, setListScrollChrome] = useState(0)
  useLayoutEffect(() => {
    if (!leftPanelEl) return
    const measure = () => setListScrollChrome(leftPanelEl.offsetWidth - leftPanelEl.clientWidth)
    measure()
    // jsdom and SSR have no ResizeObserver; the synchronous measure above is
    // all those environments can offer, and constructing it unguarded throws.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(leftPanelEl)
    return () => observer.disconnect()
  }, [leftPanelEl])
  // Scopes the pane-level paste listener below to this component's own DOM,
  // so a paste anywhere else on the page (a different modal, the top nav)
  // never reaches it.
  const panelsRootRef = useRef<HTMLDivElement>(null)
  // A paste of 2+ lines caught on the pane, held for the reader's confirmation
  // (design brief §4d) — never applied on its own. `null` means no offer is
  // showing.
  const [pasteOffer, setPasteOffer] = useState<string[] | null>(null)
  // What had focus the instant `+ Field` opened (brief §22 desktop pass 2) —
  // the field the reader was in for the `⌘⇧F` path, the trigger button
  // itself for a click. Captured here, in the toggle handler below, rather
  // than inside `FieldTypeChooser`: React applies the popover's own
  // `autoFocus` during the same commit that mounts it, ahead of any effect
  // the popover itself could run, so by the time an effect there read
  // `document.activeElement` it would already be reading what `autoFocus`
  // just moved focus TO.
  const fieldChooserOpenerRef = useRef<HTMLElement | null>(null)

  // An unsaved new card (⌘⏎, brief §22 — same pattern `DeckMobileFeed` uses
  // for `+ Card`). It cannot be POSTed empty — every side's content must be
  // non-empty server-side, Term included — so it starts as a local row and is
  // created by `ActiveVocabCard`'s own commit as soon as Term has content.
  // `savedId` exists purely so the refetched copy is not rendered twice while
  // both are briefly on screen; the id stays empty in the synthetic card so
  // `ActiveVocabCard` reads that as "the first commit is a POST".
  //
  // `insertIndex` is the deck index an insert divider asked for, or `null` for
  // the plain append `⌘⏎` makes. It is carried on the draft rather than in its
  // own state because it is a property OF this particular unsaved card: every
  // path that retires the draft (`selectField`, `goToCard`, `handleEscape`)
  // drops the position with it, which is exactly right — the position belongs
  // to the card being written, not to the panel.
  const [draft, setDraft] = useState<{
    key: string
    savedId: string | null
    insertIndex: number | null
  } | null>(null)
  const draftCard = useMemo<Card | null>(() => (draft ? {
    id: '', deck_id: deckId, position: 0, sides: [],
    mastery_level: null, share_token: null,
    created_at: '', updated_at: '',
  } : null), [draft, deckId])

  // The left list never shows the same card twice while the draft's own POST
  // and the deck's refetch are both in flight.
  const displayCards = useMemo(
    () => (draft?.savedId ? cards.filter((c) => c.id !== draft.savedId) : cards),
    [cards, draft],
  )

  const selectedCard = useMemo(() => {
    if (!selected) return null
    if (selected.cardId === DRAFT_CARD_ID) return draftCard
    return cards.find((c) => c.id === selected.cardId) ?? null
  }, [cards, selected, draftCard])

  /**
   * The reference panel follows the card being read. While it is open for a
   * card: a different selected card retargets it, an edited Term refreshes it,
   * and clearing the selection closes it. A selection the list is not showing
   * (a narrowing hid it) leaves it as it is — the selection still exists, and
   * the pane says so. A free term (a synonym clicked inside the panel) is not
   * tied to a card and is left alone.
   */
  const selectedTerm = selectedCard ? (findTermSide(selectedCard.sides)?.content ?? '') : null
  useEffect(() => {
    if (referenceCardId === null) return
    if (!selected || selected.cardId === DRAFT_CARD_ID) { onCloseReference(); return }
    if (!selectedCard || selectedTerm === null) return
    onShowReference(selectedCard.id, selectedTerm)
  }, [referenceCardId, selected, selectedCard, selectedTerm, onShowReference, onCloseReference])

  /**
   * The selection outlives a narrowing, and the pane says so.
   *
   * A selection whose card is not in the list resolves to `null` above.
   * Nothing is cleared, and that is deliberate: clearing would be a setState
   * inside an effect, and it would throw the selection away when a search
   * merely HIDES the card, so lifting the query would not bring the reader's
   * card back. Selection is the screen's single notion of the current card
   * (`utils/deckEnterLadder`), and a query is a way to look around, not a way
   * to deselect.
   *
   * What was wrong is what the screen then SHOWED: the pane fell through to
   * "Pick a card to read it" — a sentence that is false while a card is picked
   * — so the screen presented as having no selection while the selection was
   * live, and Enter went on acting on it (see the ladder's `out-of-view` rung).
   * The two states are now distinguished:
   *
   *  • **hidden by a narrowing** (`selectionHidden` below) — the card is still
   *    open, this list does not contain it, and the pane names it and says
   *    which narrowing to lift. No row is marked, which is correct: the row
   *    that would carry the mark is exactly what is missing.
   *  • **no narrowing and still unresolved** — the card is gone (deleted, or
   *    moved to another deck) rather than hidden. There is nothing to lift and
   *    nothing to come back to, so the screen presents as nothing-selected,
   *    which is the truth. The stale id is inert: every rung that could act on
   *    it now checks `selectionInView` first.
   *
   * `lastResolvedCardRef` is how the hidden state can name the card the list no
   * longer holds. It is a cache of what the pane last rendered, not a second
   * notion of what is current: it is only ever read while `selected` points at
   * that same id, and it cannot go stale against an edit, because editing a
   * card requires it to be in view, which is the state that refreshes it.
   */
  const lastResolvedCardRef = useRef<Card | null>(null)
  useEffect(() => {
    if (selectedCard) lastResolvedCardRef.current = selectedCard
  }, [selectedCard])
  const selectionInView = !!selectedCard
  const hiddenCard = !selectionInView
    && selected != null
    && lastResolvedCardRef.current?.id === selected.cardId
    ? lastResolvedCardRef.current
    : null
  // Hidden only while something is actually narrowing the list. Without a
  // narrowing there is nothing to lift, so the state would be a message with
  // no way out — see the second bullet above.
  const selectionHidden = hiddenCard && listNarrowing ? { card: hiddenCard, by: listNarrowing } : null

  // Keyed on the CARD, not on `selected`: moving the caret between fields of
  // the same card leaves its draft — and therefore any pending field Undo —
  // intact, so only opening a different card retires it.
  const openCardId = selectedCard?.id ?? null
  useEffect(() => { onSelectionChanged() }, [openCardId, onSelectionChanged])

  // ── Pane-level paste recognition (design brief §4c/§4d) ──────────────────
  //
  // A paste event targets `document.activeElement`, which for "nowhere in
  // particular" is `<body>` — a `<div>` never receives it just by being an
  // ancestor of the click that came before, so this listens at the document
  // and filters instead of attaching to a container that would silently miss
  // most of what "anywhere on this pane" needs to mean. Two guards keep it
  // from ever competing with an ordinary field paste: an editable target (a
  // real input, textarea or `contenteditable`) is always let through
  // untouched, and the fallback-to-body case is scoped to while THIS
  // component is mounted, i.e. this deck's own pane, not the whole app.
  useEffect(() => {
    if (!isOwner) return
    function isEditableTarget(el: Element | null): boolean {
      if (!el) return false
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return true
      return (el as HTMLElement).isContentEditable === true
    }
    function handlePaste(e: ClipboardEvent) {
      const active = document.activeElement
      if (isEditableTarget(active)) return
      const root = panelsRootRef.current
      const withinPane = active === document.body || (!!root && root.contains(active))
      if (!withinPane) return
      const text = e.clipboardData?.getData('text/plain') ?? ''
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
      // One line (or none) is not "a list" — an ordinary paste with nowhere
      // in particular focused has nothing here to catch it, so it simply does
      // nothing rather than offering to create a single card from it.
      if (lines.length < 2) return
      e.preventDefault()
      setPasteOffer(lines)
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [isOwner])

  function handleConfirmPasteImport() {
    if (!pasteOffer) return
    onPasteWordList(pasteOffer)
    setPasteOffer(null)
  }

  // Escape discards the offer the same as its own button — scoped to while
  // the offer is showing, so it never competes with `handleEscape`'s own
  // Escape handling inside an active card.
  useEffect(() => {
    if (!pasteOffer) return
    // `preventDefault` is how a more specific Escape owner tells the screen's
    // ladder it has claimed the press (`utils/deckEscapeLadder`) — without it
    // this would discard the offer AND the ladder would dismiss the card
    // underneath in the same press.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      setPasteOffer(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pasteOffer])

  /**
   * The link, drawn from the left.
   *
   * Two cases, and the second is the common one: clicking a field of the card
   * the panel is ALREADY showing must move the caret, and no prop change can do
   * that — `ActiveVocabCard` reads `initialFieldKey` once, on mount. So the
   * same-card case goes through the imperative handle. The different-card case
   * remounts (the `key` below is the card id) and the mount effect places the
   * caret, which is the path the phone already uses.
   */
  /**
   * Is the card the panel is opened on in the EDITING state?
   *
   * Three ways in, and the deck-level one is deliberately first: it is a mode
   * the reader turned on, so it outranks any per-card state. The draft is
   * always editing — a card created a moment ago has no content to read
   * (ADR-067 amendment: "a newly created card opens in editing regardless").
   * A reader who does not own the deck can never reach any of the three.
   */
  /**
   * The subset of the above that a per-card action can actually END: the
   * draft, or this one card put into editing by the panel's own `Edit`.
   *
   * Deck mode is deliberately NOT in it. While the mode is on, editing is not
   * a state of this card at all — it is the mode's, and ending it per-card is
   * impossible: `endEditSession` would clear `editingCardId`, the mode would
   * re-derive `editingSelected` as true on the very next render, and nothing
   * would change. That is not hypothetical either — it is exactly what an
   * earlier cut of the Escape ladder did, and Escape became a no-op that
   * swallowed every press while deck mode was on, because rung 2 kept
   * claiming a level it could not act on. The ladder asks THIS question, so
   * that in deck mode the press falls through to rung 4 (leave the mode) and
   * the reader gets out.
   */
  const perCardEditSession = isOwner && !!selectedCard && (
    selected?.cardId === DRAFT_CARD_ID
    || (selected?.cardId != null && selected.cardId === editingCardId)
  )
  const editingSelected = isOwner && !!selectedCard && (deckEditMode || perCardEditSession)

  /** Whether opening `cardId` lands in EDITING rather than reading — the deck
   * mode, the unsaved draft, or the card that is already mid-edit. Extracted
   * because two callers need the same answer: `selectField`, to decide whether
   * to honour a field key, and `selectForReading`, to decide whether moving
   * focus to the row would be stealing a caret the open is about to place. */
  function opensIntoEditing(cardId: string): boolean {
    return isOwner && (deckEditMode || cardId === DRAFT_CARD_ID || cardId === editingCardId)
  }

  /**
   * Open a card, and put focus where the model says it goes: on that card's
   * row (`utils/deckEnterLadder` — inside the list, focus follows selection).
   *
   * This is what makes the three routes to a card land in the same state. A
   * click already focuses the row it hits; ⏎ from the scrollport and ↑/↓ from
   * a row did not, so the same selection used to come with focus in three
   * different places, and the next Enter meant three different things.
   *
   * Not applied when the open lands in EDITING — there the card's own mount
   * effect is placing the caret in a field, and focusing the row behind it
   * would take the caret straight back out.
   */
  const selectForReading = useCallback((cardId: string) => {
    const willEdit = opensIntoEditing(cardId)
    selectField(cardId, TERM_FIELD_KEY)
    if (willEdit) return
    // `focus()` scrolls its target into view, which is wanted here (unlike
    // `focusListScrollport`): ↑/↓ walking a long deck must bring the row it
    // lands on into view.
    requestAnimationFrame(() => rowElsRef.current.get(cardId)?.focus())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, deckEditMode, editingCardId])

  /**
   * ↑ / ↓ with a row focused — move the SELECTION one card, for reading, with
   * focus riding along (`selectForReading`). This is the list's own movement,
   * and it is what makes one tab stop per list workable: without it, Tab was
   * the only way from row to row, which is why every row had to be a tab stop
   * and why the pane sat one press per card away on a large deck.
   *
   * Holds still at either end rather than wrapping: a wrap would move the
   * reader the length of the deck on a press that looked like one step.
   */
  const stepSelection = useCallback((fromCardId: string, dir: 1 | -1) => {
    const i = displayCards.findIndex((c) => c.id === fromCardId)
    if (i < 0) return
    const next = i + dir
    if (next < 0 || next >= displayCards.length) return
    selectForReading(displayCards[next].id)
  }, [displayCards, selectForReading])

  /**
   * The list's single tab stop (roving tabindex): the selected row, or the
   * first row when the selection is elsewhere — nothing selected, or the
   * unsaved draft, which has no row of its own.
   *
   * One stop, not one per card, is what keeps ⇥ usable for its ordinary job:
   * the pane and the controls past the list are a fixed number of presses
   * away whatever the deck's size, instead of one press per card (a 1029-card
   * system deck put them a thousand presses away, and ADR-067 Decision 6 says
   * that list is never virtualised, so every one of those rows is real).
   */
  const tabStopCardId = (
    selected?.cardId && displayCards.some((c) => c.id === selected.cardId)
      ? selected.cardId
      : displayCards[0]?.id ?? null
  )


  function selectField(cardId: string, fieldKey: string | null) {
    // Opening a different card retires the draft — by the time this runs its
    // Term was either committed (an ordinary card now) or blank (nothing to
    // keep), the same rule `DeckMobileFeed` applies when its own draft is
    // left behind. Done here, at every place `selected` can change AWAY from
    // the draft, rather than in a reactive effect: a `setState` synchronised
    // off another `setState` is the cascading-render shape effects exist to
    // avoid, and every transition is already a discrete event with a name.
    if (cardId !== DRAFT_CARD_ID) setDraft(null)
    // ADR-067 amendment, the first thing on its do-not-reintroduce list: a
    // click must not both select a card and place the caret in the field under
    // the pointer. The field key is followed only where editing is ALREADY in
    // force for the card being opened — the deck mode, the draft, or this same
    // card mid-edit — and dropped everywhere else, so an ordinary click opens
    // the card to be read and nothing takes the caret.
    const willEdit = opensIntoEditing(cardId)
    // Leaving a card returns it to reading: the single-card editing state is
    // about the card the reader chose to edit, and carrying it onto the next
    // card they merely clicked would be the every-click-edits behaviour this
    // amendment removes. The deck mode is untouched — it is not this
    // component's to turn off.
    if (cardId !== editingCardId) {
      setEditingCardId(null)
      setFieldChooserOpen(false)
    }
    setSelected((prev) => ({ cardId, fieldKey: willEdit ? fieldKey : null, nonce: (prev?.nonce ?? 0) + 1 }))
  }

  /** The panel's own `Edit` — the explicit per-card action that enters editing
   * (ADR-067 amendment). The caret goes to Term rather than to wherever the
   * pointer happens to be: the click that opened this card for reading was
   * deliberately not allowed to choose a field, so there is nothing to honour
   * here and Term is where a card starts. */
  // `useCallback` because the Enter ladder's listener depends on it: a new
  // identity every render would tear down and re-add that listener on every
  // render of the panel.
  const startEditingSelected = useCallback(() => {
    const cardId = selected?.cardId
    if (!cardId) return
    setEditingCardId(cardId)
    setSelected((prev) => (prev ? { ...prev, fieldKey: TERM_FIELD_KEY, nonce: prev.nonce + 1 } : prev))
  }, [selected])

  /**
   * The deck desktop screen's single Enter ladder — the descending twin of the
   * Escape ladder in `DeckDetailPage`. The order, the model behind it, and why
   * rung 4 deliberately does not claim the press live in
   * `utils/deckEnterLadder`; this listener only supplies the current state and
   * performs the action for whichever rung comes back.
   *
   * ONE listener, at the window, for every focus location. It used to be two
   * rules that could not see each other — this effect, gated on `document.body`
   * or the list scrollport holding focus, and each ROW's own `onKeyDown`, which
   * unconditionally re-selected its card. That is why arriving at a card by
   * Tab or by click made `Enter` stop meaning "edit": focus was on a row, so
   * this effect bailed and the row's rule answered instead, with the same
   * answer every time. The row no longer answers for itself; it reports its
   * focus into rung 3 and the one order decides.
   */
  useEffect(() => {
    if (pasteOffer) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Enter' || e.defaultPrevented) return
      // `defaultPrevented` covers a more specific owner INSIDE the screen, the
      // same convention the Escape ladder follows. What it does not cover is
      // an ordinary control that Enter activates natively (a button, a link):
      // those are not "a card field", but the press is theirs, and rung 3
      // would otherwise steal it on the way past.
      const active = document.activeElement as HTMLElement | null
      if (active && active !== document.body && active.closest('button, a[href], [role="menuitem"]')) return
      const rung = resolveDeckEnterRung({
        // This component's own overlays (the paste offer, the field chooser)
        // and whatever the page has layered over the screen.
        overlayOpen: !!pasteOffer || fieldChooserOpen || pageOverlayOpen,
        // The caret's own field, read off the DOM rather than off `selected`:
        // `selected.fieldKey` says where the caret was ASKED to go, and this
        // rung has to know where it actually is (ADR-067's Enter-in-a-field
        // decision is about the live caret, not about an intent).
        caretInField: !!active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA'),
        focusedRowCardId: active ? (active.closest('[data-card-id]')?.getAttribute('data-card-id') ?? null) : null,
        selectedCardId: selected?.cardId ?? null,
        // The pane can render the selection — false while a narrowing has put
        // the selected card out of the list. The ladder's `out-of-view` rung
        // reads this and yields, so a press cannot arm an edit session on a
        // card nobody can see.
        selectionInView,
        editSessionOpen: editingSelected,
        hasCards: displayCards.length > 0,
        canEdit: isOwner,
      })
      switch (rung) {
        // Not ours: the overlay's, the field's (a line break in side B, nothing
        // in Term — ADR-067, 2026-07-30), or nobody's.
        case 'overlay': case 'field': case 'out-of-view': case 'edit': case 'none': return
        case 'focus-row': {
          const cardId = active?.closest('[data-card-id]')?.getAttribute('data-card-id')
          if (!cardId) return
          e.preventDefault()
          selectForReading(cardId)
          return
        }
        case 'read': e.preventDefault(); startEditingSelected(); return
        case 'pick-first': e.preventDefault(); selectForReading(displayCards[0].id); return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [
    pasteOffer, fieldChooserOpen, pageOverlayOpen, selected, displayCards, selectionInView,
    editingSelected, isOwner, selectForReading, startEditingSelected,
  ])

  /** The panel's own `Done` — back to reading, same card still open.
   * Unmounting `ActiveVocabCard` flushes whatever was mid-edit, exactly as ⎋
   * does. Only ever rendered in the single-card case (see its call site): the
   * deck mode's off switch is in the header bar, and a `Done` that appeared to
   * end editing and then re-entered it on the reader's next click would be a
   * control that lies about what it did. */
  function stopEditingSelected() {
    setEditingCardId(null)
    setFieldChooserOpen(false)
    setSelected((prev) => (prev ? { ...prev, fieldKey: null, nonce: prev.nonce + 1 } : prev))
  }

  // Deliberately depends on the PRIMITIVES `selected` carries, not on the
  // `selected` object itself (nor on `selectedCard`) — two different unrelated
  // updates would otherwise re-fire this and yank the caret back to a stale
  // field:
  //  1. A field commit (every blur) PATCHes the card, whose `onSuccess`
  //     invalidates the `['cards', deckId]` query (`useUpdateCard` in
  //     `useCards.ts`) — a background refetch lands on essentially every Tab
  //     press, giving `selectedCard` (and, before this fix, `cards` itself) a
  //     new object identity with no change to the selection.
  //  2. `handleFocusedFieldType` clears `selected.missingType` via
  //     `setSelected((prev) => ({ ...prev, missingType: undefined }))` the
  //     moment a walk's fallback-Term landing is followed by a real field
  //     taking focus (brief §13's missing-field affordance retiring itself).
  //     That spread makes a NEW `selected` object with the SAME `fieldKey` —
  //     depending on the object would re-run this effect and call
  //     `focusField` with a `fieldKey` that has been stale since the reader's
  //     very next ⇥, discarding it.
  // `cardId` is included so a card-boundary crossing (§22's ⇥ past the last
  // field / ⇧⇥ before Term) still re-fires even on the rare occasion its
  // `nonce` bump coincides with an unrelated render — belt over suspenders,
  // not load-bearing on its own. `activeCardRef.current` is read fresh, not
  // as a dependency: React attaches a child's ref and runs the child's own
  // mount effect before this parent effect on every commit, so by the time
  // this runs the ref already names the right instance.
  const selectedFieldKey = selected?.fieldKey ?? null
  const selectedCardId = selected?.cardId ?? null
  const selectedNonce = selected?.nonce ?? null
  useEffect(() => {
    if (!selectedFieldKey) return
    activeCardRef.current?.focusField(selectedFieldKey)
  }, [selectedCardId, selectedFieldKey, selectedNonce])

  // ── The keyboard cycle's card-boundary crossings (brief §22) ─────────────
  //
  // The type of field the walk is CARRYING — not simply the type last
  // focused. The distinction matters in exactly one case: landing on a card
  // that lacks the type falls back to Term, and if that fallback were let
  // become the carried type, the next boundary crossing would walk Terms
  // instead of resuming whatever field the author was actually correcting.
  // `skipNextTypeReportRef` is what keeps the fallback's own focus report
  // from overwriting it — identical to `DeckMobileFeed`'s arrows, because
  // this is the same walk wearing a different trigger.
  const walkTypeRef = useRef<FieldType>(TERM_FIELD_TYPE)
  const skipNextTypeReportRef = useRef(false)
  const handleFocusedFieldType = useCallback((type: FieldType) => {
    if (skipNextTypeReportRef.current) { skipNextTypeReportRef.current = false; return }
    walkTypeRef.current = type
    setSelected((prev) => (prev?.missingType ? { ...prev, missingType: undefined } : prev))
  }, [])

  /** Opens `target`, carrying `type` onto it — the desktop twin of
   * `DeckMobileFeed`'s `goToCard`. Always a real card (`target` comes from
   * `cards`, never the draft), so the draft retires the same as any other
   * cross-card move. */
  const goToCard = useCallback((target: Card, type: FieldType) => {
    setDraft(null)
    // Every caller is a keyboard walk from INSIDE a field, so the reader is
    // already editing and the card they cross into opens editing too —
    // otherwise ⇥ past the last field would drop them into a reading panel
    // with the caret nowhere, breaking the cycle brief §22 built. This is an
    // explicit action on the card in exactly the sense the amendment means;
    // what it must not become is a click that does the same.
    setEditingCardId(target.id)
    const key = findFieldKeyOfType(target, type)
    skipNextTypeReportRef.current = key === null
    setSelected((prev) => ({
      cardId: target.id,
      fieldKey: key ?? TERM_FIELD_KEY,
      nonce: (prev?.nonce ?? 0) + 1,
      missingType: key === null ? type : undefined,
    }))
  }, [])

  // What the active card renders in the missing field's place, mirroring
  // `DeckMobileFeed`'s `missingFieldDescriptor` — Term can never be "missing",
  // so a walk carrying it can never reach this.
  const missingFieldDescriptor = useMemo(() => {
    const type = selected?.missingType
    if (!type || type.kind === 'term') return null
    return {
      kind: type.kind,
      label: type.customName ?? undefined,
      displayName: fieldTypeLabel(type, ce),
    }
  }, [selected?.missingType, ce])

  /** Opens the right panel on a new unsaved card, caret in Term. One
   * implementation, two triggers: `⌘⏎` (append, brief §22) and an insert
   * divider (a chosen position, §22's hairline-with-a-plus). They differ by
   * one number and nothing else — which is the whole point of the divider no
   * longer opening a surface of its own. */
  const startDraft = useCallback((insertIndex: number | null) => {
    setFieldChooserOpen(false)
    walkTypeRef.current = TERM_FIELD_TYPE
    skipNextTypeReportRef.current = false
    setDraft({ key: `draft-${Date.now()}`, savedId: null, insertIndex })
    setSelected((prev) => ({ cardId: DRAFT_CARD_ID, fieldKey: TERM_FIELD_KEY, nonce: (prev?.nonce ?? 0) + 1 }))
  }, [])

  /** ⌘⏎ — a new card below, caret in Term (brief §22). */
  const handleNewCardBelow = useCallback(() => { startDraft(null) }, [startDraft])

  // The page's own `+ Card`, which cannot reach `startDraft` directly.
  //
  // The baseline is a literal 0, not the nonce this component happened to
  // mount with, and that is load-bearing for the EMPTY deck: there the button
  // is what mounts these panels in the first place (before it there are no
  // rows and no panel), so the very first render already carries a nonce of 1
  // and must open a draft. Baselining on the mount value would make that
  // press do nothing at all. On a deck that already has cards the panels are
  // mounted at 0 and nothing opens until a press moves it.
  const seenNonceRef = useRef(0)
  useEffect(() => {
    if (newCardNonce === seenNonceRef.current) return
    seenNonceRef.current = newCardNonce
    startDraft(null)
  }, [newCardNonce, startDraft])

  // ⇥ past the last field and ⇧⇥ before Term both need "this card's position
  // among its SIBLINGS". A real card has one. The draft does not: it sits
  // BETWEEN two rows rather than on one, so its two neighbours are named
  // directly below instead of being derived from a single index by ±1, which
  // cannot express "after row i-1 AND before row i".
  const selectedIndex = selectedCard && selected?.cardId !== DRAFT_CARD_ID
    ? indexOf(selectedCard)
    : null
  // The deck index the draft will occupy — the divider's own index, or one
  // past the last loaded row for an appended `⌘⏎` draft.
  const draftAt = draft ? draft.insertIndex ?? cards.length : null

  /** ⇥ pressed on the last field — the next stop is the next card, never the
   * card's own `+ Field`/`Fill` row (brief §22: "after the last field — the
   * next card", not "the card's action row"). No next card in the loaded
   * window is not an invitation to guess at one — the reader still has ⌘⏎ for
   * that — so this simply holds still rather than falling anywhere. */
  const handleTabPastLastField = useCallback(() => {
    // The draft's next stop is the row it will sit ABOVE — which for an
    // appended draft is past the end, so it holds still exactly as before.
    if (draftAt !== null) {
      if (draftAt >= cards.length) return
      goToCard(cards[draftAt], walkTypeRef.current)
      return
    }
    if (selectedIndex === null) return
    const target = stepTarget(selectedIndex, 1, cards.length)
    if (target === null) return
    goToCard(cards[target], walkTypeRef.current)
  }, [draftAt, selectedIndex, cards, goToCard])

  /** ⇧⇥ pressed on Term — the card before this one, same carried type. */
  const handleShiftTabBeforeTerm = useCallback(() => {
    // The draft's previous stop is the row it will sit BELOW.
    if (draftAt !== null) {
      const prev = draftAt - 1
      if (prev < 0 || prev >= cards.length) return
      goToCard(cards[prev], walkTypeRef.current)
      return
    }
    if (selectedIndex === null) return
    const target = stepTarget(selectedIndex, -1, cards.length)
    if (target === null) return
    goToCard(cards[target], walkTypeRef.current)
  }, [draftAt, selectedIndex, cards, goToCard])

  /**
   * Where focus goes after a dismissal — the list's own scrollport.
   *
   * NOT the page body: from body the next ⇥ restarts at the top of the
   * document (the sidebar), which throws away a keyboard reader's place in a
   * long deck. NOT the row that was just left either, even though it is still
   * on screen: a focused row owns `Enter` for itself (it reopens that card),
   * which would quietly contradict whichever `Enter` hint the pane is
   * advertising at that moment — "open the first card" in the empty state,
   * "edit" in the reading state. The scrollport is focusable (`tabIndex={-1}`)
   * without claiming any key, so both hints stay true and ⇥ continues from
   * the list.
   *
   * `preventScroll` is load-bearing, not a precaution: `focus()` scrolls its
   * target into view by default, and this element is the scroller itself, so
   * the default would yank a reader who had scrolled far down the deck back
   * toward the top — exactly the "lose the user's place" this dismissal must
   * not do.
   */
  const focusListScrollport = useCallback(() => {
    requestAnimationFrame(() => leftPanelRef.current?.focus({ preventScroll: true }))
  }, [])

  /**
   * ⎋ rung 2 (`utils/deckEscapeLadder`) — end the EDIT SESSION, and only that.
   *
   * This used to clear the selection as well, so one press left an open card
   * and landed on the empty pane. That is two levels at once: the reader who
   * presses Escape in an editor is asking to stop editing, not to put the card
   * away, and there was then no press that stopped at reading. Now Escape
   * hands the card back in its reading state and a second press dismisses it
   * (rung 5, `clearSelection`).
   *
   * Unmounting `ActiveVocabCard` still flushes whatever was mid-edit — the
   * same commit "Done" makes on mobile — because the reading branch renders a
   * different component, so the editor unmounts either way.
   *
   * The DRAFT is the one case that does clear the selection, and it is not an
   * exception to the rule above: ending an unsaved new card's edit session
   * removes the card itself, so there is no reading state left to hand back
   * and nothing for the selection to point at. `selected` is cleared with it
   * rather than left dangling at `DRAFT_CARD_ID`.
   *
   * Returns whether it acted. `false` means there was no per-card session to
   * end — deck mode is on, and the press belongs to a lower rung. Both
   * callers need that answer: the ladder to keep walking, and the active
   * card's own key handler to decide whether to `preventDefault()` (if it
   * claimed a press it did not act on, the ladder below would never run and
   * Escape would do nothing at all while deck mode was on).
   */
  const endEditSession = useCallback((): boolean => {
    if (!perCardEditSession) return false
    const wasDraft = selected?.cardId === DRAFT_CARD_ID
    setDraft(null)
    setEditingCardId(null)
    setFieldChooserOpen(false)
    if (wasDraft) setSelected(null)
    // Back to reading, same card, caret nowhere — the `fieldKey` must be
    // dropped or the focus effect would put the caret straight back into a
    // field the reader just left.
    else setSelected((prev) => (prev ? { ...prev, fieldKey: null, nonce: prev.nonce + 1 } : prev))
    // Focus follows the selection (`utils/deckEnterLadder`): Escape hands the
    // card back in its reading state, so focus belongs on that card's ROW —
    // which is where ⏎ and ↑/↓ then both mean what the model says. Only the
    // draft, which leaves no card behind, falls back to the scrollport.
    //
    // This used to go to the scrollport in both cases, on the grounds that a
    // focused row "owns Enter for itself" and would contradict the pane's
    // "Enter to edit" hint. That reason is gone: the row no longer answers
    // Enter — the ladder does, from the selection — so the row is now exactly
    // where the hint is true.
    const backTo = !wasDraft && selected?.cardId ? rowElsRef.current.get(selected.cardId) : undefined
    if (backTo) requestAnimationFrame(() => backTo.focus({ preventScroll: true }))
    else focusListScrollport()
    return true
  }, [perCardEditSession, selected, focusListScrollport])

  /**
   * ⎋ rung 5, and the empty-space click — return the pane to its resting
   * state. Deliberately touches nothing but the selection: not the deck mode,
   * not the search, not the list's scroll position.
   */
  const clearSelection = useCallback(() => {
    setDraft(null)
    setEditingCardId(null)
    setFieldChooserOpen(false)
    setSelected(null)
    focusListScrollport()
  }, [focusListScrollport])

  useImperativeHandle(ref, () => ({
    // `perCardEditSession`, not `editingSelected` — see that flag's comment.
    isEditSessionOpen: () => perCardEditSession,
    endEditSession,
    hasSelection: () => selected !== null,
    clearSelection,
  }), [perCardEditSession, endEditSession, selected, clearSelection])

  /**
   * The other dismissal: a click on empty space in the list column, or in the
   * gutter between the two panes.
   *
   * The whole column is NOT a click target. `e.target === e.currentTarget` is
   * the entire test, and it is what keeps this from swallowing anything: a
   * row, a checkbox, a drag handle, an overflow menu, the paste offer and the
   * loading sentinel are all DESCENDANTS of these two elements, so a click on
   * any of them fails the test without needing to be enumerated. Only the
   * background boxes themselves — the scrollport below the last row, and the
   * shell's flex gap — ever pass it, and neither has anything drawn on it.
   *
   * Two guards beyond that, each for a gesture that would otherwise read as a
   * background click:
   *
   *  • A TEXT SELECTION DRAG that starts on a row and releases on the
   *    background. `click` fires on the nearest common ancestor, which is one
   *    of these two elements, so the target test alone would pass. The
   *    mousedown must therefore have landed on the same background element —
   *    `bgMouseDownRef` — and any surviving non-collapsed selection vetoes the
   *    dismissal outright, which also covers a drag that both starts and ends
   *    on the background.
   *  • The SCROLLBAR. It lives inside the scrollport's padding box, so a click
   *    on it reports this element as the target. `clientWidth` excludes the
   *    scrollbar gutter, so an `offsetX` at or past it is the gutter, never
   *    content.
   *
   * `button !== 0` drops the right-click that opens a context menu.
   */
  // Records the NODE the press landed on, not whether that node was one of
  // the two backgrounds. Both backgrounds share this one ref and the scrollport
  // is a descendant of the shell, so a press on the scrollport runs the
  // scrollport's handler and then the shell's: storing a currentTarget-relative
  // verdict let the shell's run overwrite the scrollport's with "not a
  // background" and the dismissal was swallowed for every click below the last
  // row (the gutter, whose press only ever reaches the shell, worked — which is
  // exactly why this needed the pair tested separately).
  const bgMouseDownRef = useRef<EventTarget | null>(null)
  const handleBackgroundMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    bgMouseDownRef.current = e.target
  }, [])
  const handleBackgroundClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    if (e.target !== e.currentTarget) return
    if (bgMouseDownRef.current !== e.target) return
    bgMouseDownRef.current = null
    if (e.nativeEvent.offsetX > e.currentTarget.clientWidth) return
    const sel = window.getSelection()
    if (sel && !sel.isCollapsed) return
    // Nothing selected is not an error — the click simply has nothing to do,
    // and must not steal focus from wherever the reader left it.
    if (!selected) return
    clearSelection()
  }, [selected, clearSelection])

  // `+ Field` (⌘⇧F or a click on the trigger) — captures the opener only on
  // the OPENING edge, not the closing one: this same handler is the toggle
  // both the shortcut and the trigger button's own `onClick` call, and a
  // second press while it's already open is a close, which must not overwrite
  // the opener a moment before `handleFieldChooserCancel` reads it back.
  function handleToggleFieldChooser() {
    if (!fieldChooserOpen) fieldChooserOpenerRef.current = document.activeElement as HTMLElement | null
    setFieldChooserOpen((o) => !o)
  }

  // `Escape` inside the popover or its × — a genuine cancel, so focus goes
  // back to whatever opened it. Deliberately NOT used for the "a type was
  // picked" path (`FieldTypeChooser`'s own `onSelect` below calls
  // `setFieldChooserOpen(false)` directly): there, the reader's real next
  // stop is the new field `addField` is about to focus, and restoring focus
  // here as well would be a second writer racing that placement — rAF runs
  // after the field-focus effect has already painted, so it would win and
  // silently undo it.
  //
  // The opener is not always still there to receive it: opening `+ Field`
  // moves focus onto the popover's own first pill, and if the field the
  // reader was in was itself an EMPTY one just added by a still-open chooser
  // (⌘⇧F pressed twice with nothing typed between), that focus move is a
  // blur on it — `ActiveVocabCard`'s own rule for a field left empty removes
  // it right then, taking the opener's DOM node with it. `.focus()` on a
  // detached node is a silent no-op, not a throw, so without the
  // `isConnected` check focus would fall through to the page body exactly
  // the way brief §22 says this loop must never leave a keyboard user. The
  // fallback is the same row `handleEscape` uses, for the same reason.
  function handleFieldChooserCancel() {
    const cardId = selected?.cardId ?? null
    setFieldChooserOpen(false)
    requestAnimationFrame(() => {
      const opener = fieldChooserOpenerRef.current
      const row = cardId ? rowElsRef.current.get(cardId) : undefined
      const target = (opener?.isConnected ? opener : null) ?? row ?? leftPanelRef.current
      target?.focus()
    })
  }

  const keyboardCycle = useMemo(() => ({
    onTabPastLastField: handleTabPastLastField,
    onShiftTabBeforeTerm: handleShiftTabBeforeTerm,
    onNewCardBelow: handleNewCardBelow,
    // The active card's own key handler calls this and `preventDefault()`s,
    // which is what stops the screen-level ladder in `DeckDetailPage` from
    // acting on the same press — see `utils/deckEscapeLadder`.
    onEscape: endEditSession,
    onSearchInDeck,
    onSearchEverywhere: openSearchEverywhere,
  }), [handleTabPastLastField, handleShiftTabBeforeTerm, handleNewCardBelow, endEditSession, onSearchInDeck, openSearchEverywhere])

  const list = (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {isOwner && !searching && <InsertDivider onInsert={() => startDraft(0)} />}
      {displayCards.map((card) => {
        const index = indexOf(card)
        // No drag on the row face: a permanent handle is not a row affordance
        // (DESIGN_SYSTEM.md §12). Reordering is its own screen (brief §4.4),
        // reached from the deck menu — `DeckReorderScreen`.
        const renderRow = () => (
          <DeckCardListRow
            card={card}
            number={numberOf(card)}
            isOwner={isOwner}
            selectedCardId={selected?.cardId ?? null}
            highlighted={highlightedCardId === card.id}
            selectedFieldKey={selected?.fieldKey ?? null}
            onSelectField={selectField}
            isSelected={isSelected(card.id)}
            isAnySelected={isAnySelected}
            onCheckboxClick={(e) => onCheckboxClick(e, card.id, index)}
            onCheckboxPointerDown={(e) => onCheckboxPointerDown(e, card.id)}
            onCheckboxPointerEnter={() => onCheckboxPointerEnter(card.id)}
            isTabStop={card.id === tabStopCardId}
            onStepSelection={stepSelection}
            rowRef={(el) => {
              if (el) {
                rowElsRef.current.set(card.id, el)
                // Last, and after the element is in place: registering can call
                // back into a reader that was waiting for this row, and that
                // reader measures the element it is handed.
                rowRegistry?.set(card.id, el)
              } else {
                rowElsRef.current.delete(card.id)
                rowRegistry?.delete(card.id)
              }
            }}
          />
        )
        return (
          // `data-card-id` stays: the page's touch paint-selection reads it off
          // the closest wrapper. The `id="card-…"` anchor and `data-card-index`
          // that used to sit beside it are gone with the document lookup they
          // existed for — nothing else read either.
          // `data-ref-trigger`: the word reference panel closes on a click
          // outside itself, but a row is how the reader moves to the next card,
          // and the panel follows that move rather than closing on it.
          <div key={card.id} data-card-id={card.id} data-ref-trigger="">
            {renderRow()}
            {isOwner && !searching && (
              <InsertDivider onInsert={() => startDraft(index + 1)} />
            )}
          </div>
        )
      })}
      {/* The load-more sentinel keeps a 1px box for the observer and gives the
          pixel back, so it takes no flow space: the list's floor inset is
          measured from the last row, and a sentinel that added to it would
          make the list's ending 1px different from the pane's. */}
      <div ref={loadMoreRef} aria-hidden="true" style={{ height: 1, marginTop: -1 }} />
      {isFetchingNextPage && (
        <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {dd.loadingMoreCards}
        </div>
      )}
    </div>
  )

  // Below the threshold, the sheet is "open" exactly when a card is selected
  // — there is no separate open/closed flag to fall out of sync with it. The
  // list dims to 28% under it (design brief §4h) but stays fully interactive:
  // clicking a dimmed row already calls `selectField` below, which is at once
  // the third way back out of the sheet (it replaces `selected`) and the way
  // into a different card, with no extra wiring needed for either.
  const sheetOpen = narrow && !!selectedCard
  // Never a second JSX branch for narrow vs wide (see the class doc): both
  // panels stay mounted in the exact same tree position across the
  // threshold, and only their CSS moves, so `ActiveVocabCard`'s draft and any
  // open field Undo survive a resize with a card open.
  /**
   * The pane's two widths (ADR-067 amendment; tokens and the rule behind them
   * in DESIGN_SYSTEM.md §9 "Deck detail pane — reading vs editing geometry").
   *
   * The detail pane is the SIZED column and the list takes the remainder, so
   * one width here is the whole geometry. The split is weighted toward the
   * pane, and the reason is what each column is FOR: the list exists to find a
   * card — a row needs enough width to be scanned, not enough to read a
   * definition across — while the work happens in the pane. The list's share
   * is therefore its floor plus whatever is spare, not a fixed majority.
   *
   * Editing is the reading measure plus the two furniture gutters a field's
   * drag handle and delete control need, so the text column is the same width
   * in both states and widening the pane widens the text, not the chrome.
   *
   * ONE clamp, applied to both states rather than to editing alone: the pane
   * asks for its state's width, and the list's floor (`--deck-list-min-width`)
   * is what it must leave behind.
   *
   * The list's floor is the one this expression treats as inviolable, and
   * `--deck-pane-width-min` is deliberately NOT in it as a second, competing
   * floor. The two are floors against different failures, and only one of them
   * is protectable in the band just above the two-pane sheet threshold, where
   * the window cannot spare both at once.
   *
   * Which failure is the harder one was re-argued on 2026-09-12, because the
   * old answer rested on a premise the accepted card row removed. It used to
   * read: a row that RE-WRAPS corrupts the list's one job, while the pane's
   * content merely gains a line. The accepted row cannot re-wrap — its slots
   * are one line each and clip with an ellipsis — so that sentence no longer
   * describes anything. The conclusion survives on a different failure: below
   * its floor the row spends the translation first and then starts truncating
   * the TERM, and a truncated term can make two cards indistinguishable in the
   * list ("establishment" and "establishments" both reaching "establishm...").
   * That is still the list failing at the one thing it exists for, and it is
   * still worse than a paragraph one line longer, which stays readable. So the
   * list keeps its guarantee unconditionally and the pane yields in that band,
   * for a reason that is now about identity rather than about wrapping.
   *
   * `--deck-pane-width-min` still describes the pane's floor and is never
   * crossed in the wide range where both fit, so the token is not dead: it just
   * isn't the tie-breaker.
   *
   * Deck mode with nothing selected holds the editing width too: the reader
   * turned on a mode in which every card opens into editing, so a pane that
   * sat at reading width until the first click would move the moment they
   * made it — the cards shifting under the pointer that is aiming at them.
   */
  const paneEditing = isOwner && (deckEditMode || editingSelected)
  const paneWanted = paneEditing ? 'var(--deck-pane-width-editing)' : 'var(--deck-pane-width-reading)'
  const rightPanelWidth = `min(${paneWanted}, 100% - var(--deck-list-min-width) - var(--deck-pane-gutter))`
  // The column (head + scroller) and the scroller inside it are two boxes now,
  // and only the second one scrolls. The head takes the scroller's own right
  // chrome as padding so that its content box and a row's are the same box —
  // see `listScrollChrome`.
  const computedLeftColumnStyle: CSSProperties = narrow
    ? { ...leftColumnStyle, flex: '1 1 100%' }
    : leftColumnStyle
  // The floor inset is bottom padding on the scroll region's own CONTENT
  // (DESIGN_SYSTEM.md §13, "The floor"), so scrolled to the end the last row's
  // surface clears the region's bottom edge by exactly the inset. An owner's
  // last row is followed by its resting `InsertDivider`, which is already part
  // of that distance, so the padding is what remains of it.
  const trailingDivider = isOwner && !searching && displayCards.length > 0
  const listFloorPadding = DECK_FLOOR_INSET - (trailingDivider ? INSERT_DIVIDER_REST_HEIGHT : 0)
  const computedLeftPanelStyle: CSSProperties = narrow
    ? { ...leftPanelStyle, paddingBottom: listFloorPadding, borderRight: 'none', paddingRight: 0,
        opacity: sheetOpen ? 0.28 : 1, transition: 'opacity 0.18s ease' }
    : { ...leftPanelStyle, paddingBottom: listFloorPadding }
  const listHeadStyle: CSSProperties = {
    flexShrink: 0,
    paddingRight: (narrow ? 0 : 6) + listScrollChrome,
    // The gap between the column-head line and the list's first row (§13's
    // "36 head + 12"). It belongs to the head and not to the scroller: put on
    // the scroller it would be scrolled away with the rows.
    marginBottom: 12,
  }
  const computedRightPanelStyle: CSSProperties = narrow
    ? {
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(480px, 100vw)',
        background: 'var(--bg-page)',
        borderLeft: '1px solid var(--border)',
        boxShadow: sheetOpen ? 'var(--shadow-float)' : 'none',
        overflowY: 'auto', padding: '16px 20px 28px',
        zIndex: 260,
        transform: sheetOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.22s ease',
        pointerEvents: sheetOpen ? 'auto' : 'none',
      }
    // The width is the only thing the two states change about the pane, and
    // the transition is declared once here rather than at each state's own
    // branch — the amendment asks the move to read as taking hold of one card,
    // not as the layout jumping, and one declaration is what makes both
    // directions agree. The list needs no transition of its own: it is sized
    // by what is left, so it follows this width frame by frame.
    : { ...rightPanelStyle, width: rightPanelWidth,
        transition: 'width var(--deck-pane-transition-duration) var(--deck-pane-transition-easing)' }

  return (
    <div
      ref={panelsRootRef}
      style={panelsShellStyle}
      // The gutter between the two panes — see `handleBackgroundClick`.
      onMouseDown={handleBackgroundMouseDown}
      onClick={handleBackgroundClick}
    >
      {/* ── Left: the deck, scannable ──────────────────────────────────────
          Two boxes, not one: the column head (search, the shown-count, `Add
          card`, and whatever narrowing chrome is up) and, under it, the one
          box that scrolls. The head is the page's own controls placed at the
          list's width — §13's "a control spans the width of what it acts on"
          — and it stands still, so this adds no scrolling region. */}
      <div data-list-column style={computedLeftColumnStyle}>
      {listHead !== undefined && listHead !== null && (
        <div data-list-head style={listHeadStyle}>{listHead}</div>
      )}
      <div
        data-list-scroll
        ref={setLeftPanel}
        tabIndex={-1}
        style={computedLeftPanelStyle}
        // The column's own background, below the last row.
        onMouseDown={handleBackgroundMouseDown}
        onClick={handleBackgroundClick}
      >
        {/* A paste of 2+ lines caught anywhere on the pane (design brief §4d)
            — shown before anything is created; sticky within the LIST'S OWN
            scroll container (not the shell, which is not itself scrollable)
            so it stays visible while the list scrolls under it. Abandoning it
            with "Discard paste" costs nothing: no card, no request, nothing
            to undo. */}
        {pasteOffer && (
          <div style={pasteOfferStyle} role="status">
            <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>
              {dd.pasteOfferTitle(pasteOffer.length)}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {dd.pasteOfferBody}
            </p>
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--text-primary)', fontFamily: 'var(--font-content)' }}>
              {pasteOffer.slice(0, 5).join(', ')}
              {pasteOffer.length > 5 && <span style={{ color: 'var(--text-secondary)' }}> {dd.pasteOfferMore(pasteOffer.length - 5)}</span>}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={handleConfirmPasteImport} style={pasteOfferPrimaryBtnStyle}>
                {dd.pasteOfferAdd(pasteOffer.length)}
              </button>
              <button onClick={() => setPasteOffer(null)} style={pasteOfferSecondaryBtnStyle}>
                {dd.pasteOfferDiscard}
              </button>
            </div>
          </div>
        )}
        {listPlaceholder ? listPlaceholder : list}
      </div>
      </div>

      {/* ── Right: the selected card, every field open — a 480px sheet over
          the list below the threshold, side by side above it (design brief
          §4h). Never unmounted by the width crossing; see `sheetOpen` above. */}
      {/* The word reference, over the reading pane's own box. Not a third
          column: the list's floor and the pane's widths are the whole geometry
          of this screen (§9), and a column beside them took its width out of
          both — at 1440px it left the pane 268px wide, under its floor. It is
          about the card being read, so it sits where that card is read, and
          the list beside it — the selected row's mark included — stays exactly
          as it was. It overlaps, so it carries elevation (§11: shadows are for
          what overlaps). Closed by its own ✕ or Escape, which returns the pane
          as it was. */}
      {referencePanel && (
        <div style={narrow ? referenceLayerNarrowStyle : { ...referenceLayerStyle, width: rightPanelWidth }}>
          {referencePanel}
        </div>
      )}
      <div data-deck-pane style={computedRightPanelStyle}>
        {narrow && selectedCard && (
          <button
            // "Back to list" closes the sheet outright, so it dismisses the
            // card rather than merely ending an edit session — the sheet IS
            // the selection at this width.
            onClick={clearSelection}
            aria-label={dd.sheetCloseAria}
            style={sheetBackLinkStyle}
          >
            {dd.sheetBackToList}
          </button>
        )}
        {selectedCard && !editingSelected ? (
          // THE READING STATE — the default for every card, and the only state
          // a deck the reader does not own has at all (a system deck cannot be
          // edited, so it gets no `Edit` below and no deck mode above).
          //
          // This branch used to be the non-owner's alone. It is now what an
          // ordinary click opens, which is the whole of the ADR-067 amendment:
          // the list on the left truncates every field to one line, so until
          // now the only way to READ a long definition was to open the card in
          // an editor and read it out of an input.
          <ReadingCardFields
            card={selectedCard}
            cardNumber={numberOf(selectedCard)}
            onEdit={isOwner ? startEditingSelected : null}
            referenceOpen={referenceCardId === selectedCard.id}
            onToggleReference={() => (
              referenceCardId === selectedCard.id
                ? onCloseReference()
                : onShowReference(selectedCard.id, findTermSide(selectedCard.sides)?.content ?? '')
            )}
          />
        ) : selectedCard ? (
          <>
            <ActiveVocabCard
              // Remount per card: a different card is a different draft, and the
              // mount effect is what places the caret in the clicked field. The
              // unsaved new card (⌘⏎) keys on its own stable id instead of
              // `selectedCard.id`, which stays `''` for its whole life — using
              // that directly would collide with a second `⌘⏎` before the first
              // has saved.
              key={selected?.cardId === DRAFT_CARD_ID ? draft?.key : selectedCard.id}
              ref={activeCardRef}
              card={selectedCard}
              deckId={deckId}
              onCreated={selected?.cardId === DRAFT_CARD_ID
                ? (created) => {
                  // `draft` is read from THIS render's closure, not from a
                  // state updater: a side effect inside `setDraft` would run
                  // twice under StrictMode and move the card twice. It is
                  // consumed exactly once either way — `onCreated` fires only
                  // on the POST, and every commit after it is a PATCH.
                  const at = draft?.insertIndex ?? null
                  setDraft((prev) => (prev ? { ...prev, savedId: created.id } : prev))
                  if (at !== null) onCardInsertedAt(created, at)
                }
                : undefined}
              deckLanguage={deckLanguage}
              // A draft bound for a chosen position wears the number it will
              // have once it lands there; an appended one wears the deck's
              // size plus one, as it always has. `insertIndex` is a position in
              // the loaded WINDOW (it comes from an insert divider), so under
              // an anchor it needs the window's own offset to become a card
              // number — the same distinction `indexOf`/`numberOf` draws above.
              cardNumber={selected?.cardId === DRAFT_CARD_ID
                ? (draft?.insertIndex === null || draft?.insertIndex === undefined
                  ? deckCardCount + 1
                  : windowStartsAtCard + draft.insertIndex)
                : numberOf(selectedCard)}
              initialFieldKey={selected?.fieldKey ?? null}
              initialCaret={null}
              onToggleFieldChooser={handleToggleFieldChooser}
              fieldChooserOpen={fieldChooserOpen}
              // §7's positioning arithmetic is a phone rule — it exists to keep
              // a field above a soft keyboard that has eaten half the viewport,
              // and there is no keyboard here. What remains is the plain case it
              // is a special case of: a field the caret has landed in must be
              // visible. `block: 'nearest'` moves the right panel only when the
              // row is actually outside it and by the least amount that fixes
              // that, so it is one motion and does not fight the reader's own
              // scrolling (ADR-067 Decision D).
              onFieldFocus={(row) => row.scrollIntoView({ block: 'nearest' })}
              onFieldBlur={() => {}}
              onFocusedFieldType={handleFocusedFieldType}
              missingFieldType={missingFieldDescriptor}
              onFieldDeleted={onFieldDeleted}
              onFieldOrderChanged={onFieldOrderChanged}
              onSaveError={onSaveError}
              onKeyboardCycle={keyboardCycle}
            />
            {/* Directly under the card, in flow: the chooser is position-
                agnostic, and on the phone it is the KEYBOARD that forces it
                into a fixed slot. There is no keyboard here, so it can simply
                follow the card's own action row where `+ Field` was pressed. */}
            {fieldChooserOpen && (
              <div style={{ marginTop: 8 }}>
                <FieldTypeChooser
                  onSelect={(kind, label) => {
                    setFieldChooserOpen(false)
                    activeCardRef.current?.addField(kind, label)
                  }}
                  onClose={handleFieldChooserCancel}
                />
              </div>
            )}
            {/* `Done` — the way back to reading for the SINGLE-CARD case, and
                deliberately absent while the deck mode is on: there, the next
                click would open the next card into editing again, so a `Done`
                here would read as an off switch for something it has no
                business turning off. The deck mode's own off switch is in the
                header bar, always visible (ADR-067 amendment). */}
            {!deckEditMode && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <button
                  onClick={stopEditingSelected}
                  aria-label={dd.editCardDoneAria}
                  style={paneDoneBtnStyle}
                >
                  {dd.editCardDone}
                </button>
              </div>
            )}
          </>
        ) : selectionHidden ? (
          // THE SELECTION IS STILL LIVE AND THIS LIST DOES NOT HOLD IT.
          //
          // The pane is the only surface that can say so: the row that would
          // have carried the mark is exactly what the narrowing removed, so a
          // screen that says nothing here is a screen claiming nothing is
          // selected while something is. It names the card, so the reader can
          // see their place was kept, and names the narrowing to lift rather
          // than offering a control of its own — clearing a search or a filter
          // is list-scoped, and §13 puts list-scoped controls in the list
          // column's head, not in the pane.
          <div style={emptyRightStyle}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {dd.selectionHiddenTitle(findTermSide(selectionHidden.card.sides)?.content ?? '')}
            </p>
            <p style={{ margin: '6px 0 14px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {selectionHidden.by.kind === 'search'
                ? dd.selectionHiddenBySearch(selectionHidden.by.query)
                : dd.selectionHiddenByFilter}
            </p>
            {selectionHidden.by.kind === 'search' ? (
              // ⎋ is not advertised for the filter: the Escape ladder's rung 3
              // clears a SEARCH, and there is no rung that lifts a chip filter
              // — so promising one here would be the pane inventing a key the
              // screen does not have.
              <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 6 }}>
                <dt style={kbdStyle}>{shortcutLabel(['Escape'])}</dt>
                <dd style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', alignSelf: 'center' }}>
                  {dd.kbdClearSearch}
                </dd>
              </dl>
            ) : (
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {dd.selectionHiddenFilterWayBack}
              </p>
            )}
          </div>
        ) : deckEditMode ? (
          // Deck mode with nothing selected. The pane says what it is for
          // rather than going blank — and, more to the point, it is already at
          // the editing width, so the first card the reader picks opens
          // without the two panes moving underneath them.
          <div style={emptyRightStyle}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {dd.deckEditEmptyTitle}
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {dd.deckEditEmptyBody}
            </p>
          </div>
        ) : (
          // Nothing selected (design pass, 2026-09-09): the surface STAYS —
          // this is the panel's own resting state, not a transient that
          // appears and disappears as the reader moves down the list — and
          // names only what it can act on: opening the first card and
          // searching the deck (`EMPTY_PANEL_HINTS`, wired above). The full
          // editing cycle this used to list here belongs to the state that
          // uses it, not to the state before a card is even open.
          <div style={emptyRightStyle}>
            {deckDescription && (
              <div data-pane-deck-description style={{ marginBottom: 18, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                  {dd.aboutDeckEyebrow}
                </p>
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.55, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                  {deckDescription}
                </p>
              </div>
            )}
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {dd.desktopPanelEmptyTitle}
            </p>
            <p style={{ margin: '6px 0 14px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {dd.desktopPanelEmptyBody}
            </p>
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 6 }}>
              {EMPTY_PANEL_HINTS.map(({ combo, labelKey }) => (
                <Fragment key={combo}>
                  <dt style={kbdStyle}>{combo}</dt>
                  <dd style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', alignSelf: 'center' }}>
                    {dd[labelKey]}
                  </dd>
                </Fragment>
              ))}
            </dl>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * THE READING STATE (ADR-067 amendment, 2026-09-08; erratum, 2026-09-09):
 * every field, in stored order, at full length, with nothing to change.
 *
 * §5.2 of the design brief describes this for the phone's expanded card, and
 * two of its clauses apply here unchanged — no field frames, no drag handles,
 * no delete controls — but not the third. The amendment also dropped field
 * labels, reasoning that a delete control sits beside each field in editing
 * and with nothing removable a label is noise; the erratum retracts that.
 * That reasoning was the card ROW's, transplanted: the row can drop labels
 * because it shows one authoritative field of each kind, in a fixed position,
 * so position alone identifies each one. This pane shows every field the
 * card holds, repeats included, in the author's own order — three passages
 * of prose in a row with nothing naming them are not quiet, they are
 * unidentified. So every field carries its label here, exactly as editing
 * does, Term included. Nothing is truncated either: this state exists in
 * order to be read, which is the one thing the left panel's clamped rows
 * cannot offer.
 *
 * **Order is the author's, in both states (erratum).** `displaySides` sorts
 * by stored position — the same order dragging a field in editing changes —
 * and this component renders exactly that order without regrouping by kind.
 * A display order of the pane's own would show a different card than the one
 * being edited, and would hide the very ordering the drag exists to set.
 *
 * Not `ActiveVocabCard` with its controls disabled — a disabled editor invites
 * an edit and then refuses it, and for a deck the reader does not own ADR-008
 * keeps the surface read-only at the endpoint anyway. Repeated fields keep
 * §5.3's subordination so a reader can still see which one Learn prompts from.
 *
 * `onEdit` is null for a deck that cannot be edited at all — a system deck has
 * no editing entry, and reading is its only state.
 */
function ReadingCardFields({
  card, cardNumber, onEdit, referenceOpen, onToggleReference,
}: {
  card: Card
  cardNumber: number
  onEdit: (() => void) | null
  referenceOpen: boolean
  onToggleReference: () => void
}) {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  const ce = tr.cardEditor
  const cb = tr.cardBubble
  const term = findTermSide(card.sides)?.content ?? ''
  // The primitive this panel actually needs — ordered non-term sides plus
  // ADR-066 repeat/authority bookkeeping — read directly rather than through
  // `listRow()`, which is shaped for the left panel's fixed-position summary
  // (dashes, glyph grouping) that this full-detail view has no use for.
  const sides = useMemo(() => displaySides(card), [card])
  return (
    // No padding of its own (design pass, 2026-09-09) — the pane's own
    // bounded surface (`rightPanelStyle`) supplies it now, for both states,
    // so this only sets the vertical rhythm between its own children.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {/* The card's own number, and the one control this state carries. The
          number is the same one the row wears on the left, so the reader can
          see which of the two panes is showing what. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={paneCardNumberStyle}>{cardNumber}</span>
        {onEdit && (
          <button onClick={onEdit} aria-label={dd.readCardEditAria} style={paneEditBtnStyle}>
            <SquarePen size={13} strokeWidth={2.2} />
            {dd.readCardEdit}
          </button>
        )}
      </div>
      {/* The pane's headline (design pass, 2026-09-09): roughly twice a
          definition's size (--space scale doesn't reach type sizes, so this
          stays a literal like every other font-size in this file), with a
          single rule underneath it separating the word from what is said
          about it — the one visual break in an otherwise flat field list,
          because the word is the one field every other one is ABOUT. Still
          carries its own label, like every field here (ADR-067 erratum). */}
      <div>
        <div style={readingFieldLabelStyle}>{ce.termLabel}</div>
        <div style={{ fontFamily: 'var(--font-content)', fontSize: 30, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.25, marginTop: 3 }}>
          {term}
        </div>
        <hr style={{ margin: 'var(--space-3) 0 0', border: 'none', borderTop: '1px solid var(--border)' }} />
      </div>
      {sides.map(({ side, kindRepeats, isValue }) => {
        // The field Learn studies (ADR-104 Decision 6): never a blank one.
        const authoritative = isValue
        const subordinate = kindRepeats && !authoritative
        return (
          <div key={side.id} style={subordinate ? subordinateRailStyle : undefined}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
              {/* No dimming for the subordinate case, deliberately: the label
                  already sits on the third text rung, whose value is the
                  quietest one that still clears AA small-text on this pane's
                  surface (index.css). Multiplying it by an opacity takes it
                  off the ramp and under the floor — 3.26:1 in dark, 2.74:1 in
                  light, at 9.5px. The demotion is carried by the two signals
                  that cost nothing legible: the rail on the field's left and
                  the stepped-down type in `ReadingSideContent`. */}
              <span style={readingFieldLabelStyle}>
                {sideKindLabel(side, ce)}
              </span>
              {/* ADR-066's authority report survives alongside the label: which
                  of two Examples Learn actually prompts from is not the field's
                  name, it is a fact about the card that a reader comparing them
                  needs. */}
              {kindRepeats && authoritative && (
                <span style={rowStudiedBadgeStyle}>{cb.studiedBadge}</span>
              )}
            </div>
            <ReadingSideContent side={side} term={term} subordinate={subordinate} caption={cb.audioFieldCaption} />
          </div>
        )
      })}
      {/* The reader's own actions on the WORD, after everything said about it
          and apart from the head row's one action (Edit, §11.6), which is about
          the card's content. Both are offered to every reader, owner or not:
          neither writes the deck. Each is a labelled control, never a glyph. */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 18, rowGap: 4 }}>
        <button
          onClick={onToggleReference}
          aria-expanded={referenceOpen}
          // Exempt from the panel's click-outside close, or pressing this to
          // close the panel would close it on mousedown and reopen it on click.
          data-ref-trigger=""
          style={paneWordActionStyle(referenceOpen)}
        >
          <BookOpen size={13} strokeWidth={2.2} />
          {tr.wordReference.triggerTitle}
        </button>
        {/* ADR-098: the learner's own "I have forgotten this" — only when the
            card has progress above the bottom of the loop. */}
        <ForgotWordButton
          cardId={card.id}
          deckId={card.deck_id}
          term={term}
          masteryLevel={card.mastery_level}
          isMastered={card.is_mastered}
        />
      </div>
      {/* The keyboard route into editing, named rather than left for the
          reader to discover (design pass, 2026-09-09) — `Enter` when this
          card is the one open and nothing more specific has focus (wired in
          the component above). Absent for a deck that cannot be edited at
          all: there is no route to name. */}
      {onEdit && (
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', fontFamily: 'var(--font-ui)' }}>
          {dd.readCardEditHint(shortcutLabel(['Enter']))}
        </div>
      )}
    </div>
  )
}

/**
 * One field's content in the reading state.
 *
 * Image and audio sides are the reason this is not one `<div>` of text:
 * their content is a URL (a data URI, most of the time), and printing it as
 * prose would fill the pane with base64 exactly where the reader expected a
 * picture. An image renders as an `<img>` and audio as a player — coding rule
 * 12, and the same treatment the phone's expanded card gives them. `link` is
 * kept as text rather than made clickable: a reading pane is for reading, and
 * an anchor here would be a second, quieter way to leave the deck.
 */
function ReadingSideContent({
  side, term, subordinate, caption,
}: {
  side: { id: string; kind?: string | null; content: string }
  term: string
  subordinate: boolean
  caption: string
}) {
  if (side.kind === 'image') {
    return (
      <img
        src={side.content}
        alt={term}
        style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', display: 'block' }}
      />
    )
  }
  if (side.kind === 'audio') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <audio controls src={side.content} style={{ width: '100%', height: 36 }} />
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{caption}</div>
      </div>
    )
  }
  return (
    <div style={{
      fontFamily: 'var(--font-content)',
      fontSize: subordinate ? 14 : 15.5,
      fontStyle: side.kind === 'example' ? 'italic' : 'normal',
      color: subordinate ? 'var(--text-secondary)' : 'var(--text-primary)',
      // `pre-wrap`, and no clamp of any kind: a definition or an example is
      // shown whole here however long it is (ADR-067 amendment's do-not list).
      lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
    }}>
      {side.content}
    </div>
  )
}

// ── One row of the left panel ──────────────────────────────────────────────

/**
 * One row of the deck list (DESIGN_SYSTEM.md §12).
 *
 * The face carries the index numeral, the three slots and the mastery column,
 * and nothing else. Three affordances the previous row carried are gone, each
 * by a recorded rule rather than by preference:
 *  - the DRAG HANDLE — not a permanent row affordance; it belongs to an
 *    explicit reorder mode, which does not exist on this surface yet;
 *  - the `···` OVERFLOW — an unlabelled mark, which §12.6 forbids on the row
 *    face outright;
 *  - the two-line clip — superseded by one line per slot.
 * The multi-select checkbox stays: it is labelled, and an affordance revealed
 * only by the gesture it advertises advertises nothing (2026-08-06).
 */
function DeckCardListRow({
  card: cardProp, number, isOwner, selectedCardId, selectedFieldKey, onSelectField, highlighted,
  isSelected, isAnySelected, onCheckboxClick, onCheckboxPointerDown, onCheckboxPointerEnter,
  rowRef, isTabStop, onStepSelection,
}: {
  card: Card
  number: number
  isOwner: boolean
  /** The list's one tab stop (roving tabindex) — see `tabStopCardId`. */
  isTabStop: boolean
  /** ↑/↓ from this row: move the selection one card in `dir`. */
  onStepSelection: (fromCardId: string, dir: 1 | -1) => void
  selectedCardId: string | null
  highlighted: boolean
  selectedFieldKey: string | null
  onSelectField: (cardId: string, fieldKey: string | null) => void
  isSelected: boolean
  isAnySelected: boolean
  onCheckboxClick: (e: React.MouseEvent) => void
  onCheckboxPointerDown: (e: React.PointerEvent) => void
  onCheckboxPointerEnter: () => void
  /** Registers this row's element so `⎋` (brief §22) can hand focus back to
   * it — the whole reason a keyboard-only author can leave a card and keep
   * going without ever touching the mouse. */
  rowRef?: (el: HTMLDivElement | null) => void
}) {
  const { tr } = useLocale()
  const dd = tr.deckDetail
  const ce = tr.cardEditor
  const cb = tr.cardBubble

  // Shown from the draft when there is an unconfirmed change, exactly as the
  // phone's collapsed row is (ADR-072 Decision 1). It matters more here than
  // there: on this layout the row and the editing panel are on screen at the
  // same time, so a row that waited for the write would sit visibly disagreeing
  // with the card being typed into beside it.
  const card = useVisibleCard(cardProp)
  const writeFailed = useCardWriteFailed(card.id)

  const term = findTermSide(card.sides)?.content ?? ''
  const row = useMemo(() => listRow(card, ce), [card, ce])
  const isCurrent = selectedCardId === card.id
  const isActive = (key: string | null) => key !== null && isCurrent && selectedFieldKey === key
  const selectFieldIfAny = (key: string | null) => (e: React.MouseEvent) => {
    if (key === null) return
    e.stopPropagation()
    onSelectField(card.id, key)
  }
  const [hovered, setHovered] = useState(false)

  // The repeat tags and attachment glyphs that share the term line's trailing
  // zone. A repeat is a labelled TYPE TAG with its count ("DEF ×2"), never a
  // bare numeral — §12.6: nothing on the row face is an unlabelled mark.
  const repeats: Array<{ key: string; label: string; count: number }> = []
  if (row.translation && row.translation.count > 1) {
    repeats.push({ key: 'tr', label: dd.rowRepeatTranslation, count: row.translation.count })
  }
  if (row.definition && row.definition.count > 1) {
    repeats.push({ key: 'def', label: dd.rowRepeatDefinition, count: row.definition.count })
  }
  if (row.example && row.example.count > 1) {
    repeats.push({ key: 'ex', label: dd.rowRepeatExample, count: row.example.count })
  }

  return (
    <div
      ref={rowRef}
      data-vocab-card=""
      // The deep link's arrival mark. It used to be a 3px `--accent-soft`
      // shadow ring; §12 forbids a shadow on a row outright, so the mark is
      // now the accent hairline alone — which stays distinguishable from
      // selection, since selection adds the leading bar and the accent
      // numeral on top of the same hairline. The attribute is the stable hook
      // for it (the mobile row, a different surface, keeps its ring).
      data-arrival-mark={highlighted ? '' : undefined}
      className="deck-list-row"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Unchanged: clicking the row's background opens the card with the caret
      // in Term; clicking a FIELD is the more specific gesture and stops there.
      onClick={() => onSelectField(card.id, TERM_FIELD_KEY)}
      // The list's one tab stop (roving tabindex) and the keyboard model that
      // governs it are unchanged — see `tabStopCardId` and
      // `utils/deckEnterLadder`.
      tabIndex={isTabStop ? 0 : -1}
      role="button"
      aria-label={term || cb.cardNumber(number)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          onStepSelection(card.id, e.key === 'ArrowDown' ? 1 : -1)
          return
        }
        if (e.key === ' ') {
          e.preventDefault()
          onSelectField(card.id, TERM_FIELD_KEY)
        }
        // Enter is the screen's ladder's, not the row's (deckEnterLadder).
      }}
      style={rowSurfaceStyle(isCurrent, hovered, highlighted)}
    >
      {/* The selected row's leading bar. Absolutely positioned INSIDE the
          row's own left inset rather than laid out as a column: a bar that
          displaced content would move the term on selection, and the term
          holding one offset down the whole list is what the row is for. This
          is also the assumption the list floor's arithmetic makes (§12). */}
      {isCurrent && <span aria-hidden="true" style={rowMarkerStyle} />}

      {isOwner && (
        // The selection affordance, present at rest (brief §23) — an
        // affordance revealed by the gesture it advertises advertises nothing
        // (fixed 2026-08-06, and not reopened here). It is a real column and
        // is counted in this row's fixed cost.
        <div
          role="checkbox"
          aria-checked={isSelected}
          aria-label={dd.selectCardAria}
          tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); onCheckboxClick(e) }}
          onPointerDown={(e) => { e.stopPropagation(); onCheckboxPointerDown(e) }}
          onPointerEnter={(e) => { e.stopPropagation(); onCheckboxPointerEnter() }}
          data-select-box=""
          style={rowCheckboxZoneStyle(isAnySelected || isSelected || hovered)}
        >
          <div style={rowCheckboxStyle(isSelected, isAnySelected || isSelected || hovered)} />
        </div>
      )}

      {/* Index numeral — a fixed column beside all three slots, on the same
          tertiary rung as the counters and the mastery fraction. Accent while
          the row is the selected one (§12.4). */}
      <span style={rowNumberStyle(isCurrent)}>{number}</span>

      <div style={rowContentStyle}>
        {/* ── Slot 1 · the term line, the scan anchor ──────────────────────
            The term never wraps. The translation shares this line and YIELDS
            FIRST (§12.5), strictly: it takes only the width the term leaves,
            so the term gives nothing up while the translation has any. */}
        <div style={rowTermSlotStyle}>
          <span style={rowTermStyle}>{term}</span>
          {row.translation ? (
            <span
              onClick={selectFieldIfAny(row.translation.key)}
              style={rowTranslationStyle(isActive(row.translation.key))}
            >
              {row.translation.content}
            </span>
          ) : (
            <span style={rowAbsenceStyle}>{dd.rowNoTranslation}</span>
          )}

          {/* The trailing zone — the row's only elastic part. `marginLeft:
              auto` is what makes it grow and shrink without ever moving the
              term, which sits at the head of the line. */}
          {(repeats.length > 0 || row.extras.length > 0 || writeFailed) && (
            <div style={rowTrailingZoneStyle}>
              {repeats.map((r) => (
                <span key={r.key} style={rowRepeatTagStyle}>
                  {r.label}
                  <span style={rowRepeatCountStyle}>{dd.rowRepeatCount(r.count)}</span>
                </span>
              ))}
              {row.extras.map((extra) => (
                <ExtraGlyphChip
                  key={extra.kind}
                  extra={extra}
                  active={isActive(extra.key)}
                  onClick={selectFieldIfAny(extra.key)}
                />
              ))}
              {/* Only after a failed write (ADR-072 Decision 4). */}
              {writeFailed && <UnsavedMark compact />}
            </div>
          )}
        </div>

        {/* ── Slot 2 · definition · exactly one line ───────────────────────
            ── Slot 3 · example · exactly one line ──────────────────────────
            Each slot is its own line and neither borrows the other's: a
            borrowed line moves the field below it, which is what the fixed
            skeleton forbids. An absent field is NAMED in the slot it would
            have occupied rather than left blank — words cannot be mistaken
            for a loading state (§12). */}
        <div style={rowBodyStyle}>
          <div style={rowBodyLineStyle}>
            {row.definition ? (
              <span
                onClick={selectFieldIfAny(row.definition.key)}
                style={rowBodyTextStyle(isActive(row.definition.key), false)}
              >
                {row.definition.content}
              </span>
            ) : (
              <span style={rowAbsenceStyle}>{dd.rowNoDefinition}</span>
            )}
          </div>
          <div style={rowBodyLineStyle}>
            {row.example ? (
              <span
                onClick={selectFieldIfAny(row.example.key)}
                style={rowBodyTextStyle(isActive(row.example.key), true)}
              >
                {row.example.content}
              </span>
            ) : (
              <span style={rowAbsenceStyle}>{dd.rowNoExample}</span>
            )}
          </div>
        </div>
      </div>

      {/* The mastery column holds its width whether or not a fraction is
          shown, so it runs straight down the list and an unstudied card's
          absence is legible rather than ragged (§12.7). NEW sits over an
          empty scale; a studied card's fraction sits over a filled one. */}
      <div style={rowMasteryColStyle}>
        <span style={rowMasteryStatusStyle}>
          {card.mastery_level === null && !card.is_mastered
            ? dd.rowMasteryNew
            : `${card.is_mastered ? 8 : Math.max(1, Math.min(8, Math.round(card.mastery_level ?? 0)))}/8`}
        </span>
        <MasteryMeter
          level={card.mastery_level}
          isMastered={card.is_mastered ?? false}
          width="var(--row-mastery-w)"
        />
      </div>
    </div>
  )
}

/**
 * Everything outside the three core kinds, as a glyph rather than text — the
 * fix for a raw attachment (an image's data URI, most visibly) being printed
 * as row content. `image`/`audio`/`link`/`dialog` are built-in, fixed-label
 * kinds, so each gets its own recognizable icon (an image gets an actual
 * thumbnail); `custom` (a `note`-kind side, or any unrecognized/legacy kind)
 * shares one generic icon since an author-chosen label cannot be identified
 * by an icon — its name(s) surface only on hover/`aria-label`. A repeat is a
 * small numeral on the glyph, never a second glyph (ADR-066: counted, not
 * printed).
 */
function ExtraGlyphChip({
  extra, active, onClick,
}: {
  extra: ListRowExtra
  active: boolean
  onClick: (e: React.MouseEvent) => void
}) {
  const title = extra.names.join(', ')
  return (
    <span
      onClick={onClick}
      title={title}
      aria-label={extra.count > 1 ? `${title} (${extra.count})` : title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0,
        cursor: 'pointer', borderRadius: 4, padding: '0 2px',
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: 'var(--row-absence)',
      }}
    >
      {extra.kind === 'image'
        ? <img src={extra.imageSrc} alt="" style={extraThumbStyle} />
        : <ExtraGlyphIcon kind={extra.kind} />}
      {/* The count sits BESIDE the glyph, on the tertiary rung — not as a
          filled badge on its corner. A badge at accent weight read as a
          status; this reads as what it is, a count (§12.6). */}
      {extra.count > 1 && <span style={extraCountStyle}>×{extra.count}</span>}
    </span>
  )
}

function ExtraGlyphIcon({ kind }: { kind: Exclude<ExtraGlyphKind, 'image'> }) {
  switch (kind) {
    case 'audio': return <Mic size={13} strokeWidth={2} />
    case 'link': return <LinkIcon size={13} strokeWidth={2} />
    case 'dialog': return <MessageSquare size={13} strokeWidth={2} />
    case 'custom': return <SquarePen size={13} strokeWidth={2} />
  }
}

// ── dnd-kit plumbing ───────────────────────────────────────────────────────

/**
 * The sortable wrapper.
 *
 * The drag props go to the ROW'S HANDLE, not to this wrapper: the row as a
 * whole is a click target — that is how a field is selected — so making all of
 * it draggable would put the two gestures in competition on the same pixels.
 * `render` takes them and places them, rather than this component cloning a
 * child and guessing which prop names it accepts.
 */
/** The insertion affordance between rows — an existing product mechanic
 * restated in the new language (§22): a hairline that grows a plus on hover.
 * The "add card here" pill is centred on the row (`left: 50%` +
 * `translateX(-50%)`), not pinned to the row's left edge — it marks a
 * position between two rows, not an item that starts at the row's inset. */
/** The divider's height at rest. Read by the list's floor arithmetic: an
 * owner's last row is followed by one, and it is part of the distance between
 * that row and the bottom of the scroll content. */
const INSERT_DIVIDER_REST_HEIGHT = 6

function InsertDivider({ onInsert }: { onInsert: () => void }) {
  const { tr } = useLocale()
  const [hovered, setHovered] = useState(false)
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', height: hovered ? 24 : INSERT_DIVIDER_REST_HEIGHT, display: 'flex', alignItems: 'center', transition: 'height 0.15s ease' }}
    >
      <div style={{ position: 'absolute', left: 0, right: 0, height: 1, background: hovered ? 'var(--accent)' : 'var(--border)', opacity: hovered ? 0.6 : 0.25 }} />
      {hovered && (
        <button
          onClick={(e) => { e.stopPropagation(); onInsert() }}
          style={{
            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 10px', background: 'var(--bg-page)', border: '1px solid var(--accent)',
            borderRadius: 'var(--radius-pill)', color: 'var(--accent)',
            fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            whiteSpace: 'nowrap', zIndex: 1,
          }}
        >
          <Plus size={11} strokeWidth={2.4} />
          {tr.deckDetail.addCardHere}
        </button>
      )}
    </div>
  )
}

// ── Styles ─────────────────────────────────────────────────────────────────


// The word reference over the reading pane (see where it renders). Wide: the
// pane's exact box — flush right, full height, the pane's own width. Narrow:
// the pane is itself a sheet at the right edge, and the layer takes the same
// slot one step above it.
const referenceLayerStyle: CSSProperties = {
  position: 'absolute', top: 0, right: 0, bottom: 0, zIndex: 5,
  display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto',
}
const referenceLayerNarrowStyle: CSSProperties = {
  position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 261,
  width: 'min(480px, 100vw)',
  display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto',
}


// The two "nothing to show yet" states (deck-mode-empty and the resting
// empty panel) — plain text inside the pane's own bounded surface above, so
// this carries no box of its own; a second, dashed one nested inside the
// pane's solid one would be two frames for one thing.
const emptyRightStyle: CSSProperties = {}

// One of the sheet's three ways back (design brief §4h) — the other two are
// ⎋ (already reaches `onKeyboardCycle.onEscape`, i.e. `handleEscape`, from
// inside any field) and a click on the dimmed list, which needs no code of
// its own here: it already calls `selectField`, replacing `selected`.
const sheetBackLinkStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', marginBottom: 10,
  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
  fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'var(--accent)',
}

// The paste offer (design brief §4d) — attached to the pane it landed on
// rather than a modal dialog, so the list stays visible and the offer reads
// as something that happened here, not an interruption from elsewhere.
const pasteOfferStyle: CSSProperties = {
  position: 'sticky', top: 0, zIndex: 5, marginBottom: 10,
  background: 'var(--accent-soft)', border: '1px solid var(--accent)',
  borderRadius: 'var(--radius-md)', padding: '12px 14px',
}

const pasteOfferPrimaryBtnStyle: CSSProperties = {
  padding: '7px 14px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none',
  borderRadius: 'var(--radius-pill)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'var(--font-ui)', whiteSpace: 'nowrap',
}

const pasteOfferSecondaryBtnStyle: CSSProperties = {
  padding: '7px 14px', background: 'var(--bg-surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)', whiteSpace: 'nowrap',
}

// The reading state's one control, and the editing state's way back out.
// Quiet: the pane's subject is the card, and neither of these is meant to
// compete with it (DESIGN_SYSTEM.md "One primary CTA per screen" — the deck
// screen's primary is Study, and this is not it). Background `--bg-page`,
// not `--bg-surface` (design pass, 2026-09-09): the pane itself is
// `--bg-surface` now (`rightPanelStyle`), and a same-surface fill on the
// button sitting inside it would leave only the border to read as a chip.
/** A word action in the reading pane — the same quiet text control the reset
 * uses, so the two read as one group. `on` is the reference panel being open
 * for this card: the label turns to the accent rather than changing wording. */
function paneWordActionStyle(on: boolean): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
    color: on ? 'var(--accent)' : 'var(--text-secondary)',
    fontFamily: 'var(--font-ui)', fontSize: 13,
  }
}

const paneEditBtnStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '4px 11px', background: 'var(--bg-page)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
  color: 'var(--text-secondary)', cursor: 'pointer',
  fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
}

const paneDoneBtnStyle: CSSProperties = {
  ...paneEditBtnStyle,
  padding: '6px 16px', color: 'var(--accent)', borderColor: 'var(--accent)',
  fontSize: 12.5,
}

// A subordinate field of a repeating kind (§5.3): a hairline rail and the
// stepped-down type in `ReadingSideContent`, alongside its own label above
// it — the rail marks the DEMOTION, the label still names the field, and
// dropping either loses a different fact. The label is not additionally
// dimmed; see its call site for why the demotion is not spent on it.
const subordinateRailStyle: CSSProperties = {
  borderLeft: 'var(--field-rule-width) solid var(--field-rule-color)',
  paddingLeft: 'var(--space-3)',
}

// The reading state's field labels (ADR-067 erratum, 2026-09-09) — the same
// eyebrow treatment `ActiveVocabCard`'s editing fields use (fontSize 9.5,
// letterSpacing 0.09em), so a field reads as the same field in either state.
// Small and tertiary (design pass, 2026-09-09) — read PAST rather than
// read, which is why this dropped a weight from its editing-state twin
// (`ActiveVocabCard`'s own field label): editing needs the label at full
// strength because a delete control sits beside it (ADR-067 erratum), but
// this state has nothing to confirm before acting on, only a name to skim.
// `--text-tertiary` is a declared token in both themes (index.css;
// DESIGN_SYSTEM.md "The text ramp") — read it bare. It used to be read as
// `var(--text-tertiary, var(--text-secondary))`, which silently rendered
// this label at the secondary rung's weight, i.e. at no step down at all.
const readingFieldLabelStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 9.5, fontWeight: 600,
  letterSpacing: '0.09em', textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
}

const kbdStyle: CSSProperties = {
  margin: 0, alignSelf: 'center',
  fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 700,
  color: 'var(--text-secondary)', background: 'var(--bg-page)',
  border: '1px solid var(--border)', borderRadius: 5,
  padding: '2px 6px', whiteSpace: 'nowrap',
}

// ── The card row's surface and slots (DESIGN_SYSTEM.md §12) ─────────────────
//
// A row is its own bounded surface at a CONSTANT height with three named
// slots. Every number here comes from a token; none is a literal, because the
// list floor's arithmetic is derived from exactly these values (§9) and a
// literal that drifted from them would move the floor without moving the
// number the floor was computed from.
//
// Both boundary marks are always drawn and each theme leans on the one that
// carries: in light the plane step against the page is almost nothing so the
// hairline carries; in dark the hairline is the faintest mark on screen so the
// plane step carries. Same tokens, opposite mechanics — index.css says which
// is which. No shadow and no interior rules: a row is the lowest rung of the
// card ladder and must never be mistakable for a deck card or for the pane.
function rowSurfaceStyle(selected: boolean, hovered: boolean, highlighted: boolean): CSSProperties {
  return {
    position: 'relative',
    boxSizing: 'border-box',
    height: 'var(--row-h)',
    display: 'flex',
    alignItems: 'stretch',
    gap: 'var(--row-col-gap)',
    padding: 'var(--row-pad-y) var(--row-pad-x)',
    borderRadius: 'var(--row-radius)',
    cursor: 'pointer',
    // Hover owns the tint; selection never tints (§12.4). The two stack
    // legibly — a cursor resting on the selected row is two facts, two marks.
    background: hovered && !selected ? 'var(--row-plane-hover)' : 'var(--row-plane)',
    border: `1px solid ${
      selected || highlighted ? 'var(--row-border-selected)'
        : hovered ? 'var(--row-border-hover)'
          : 'var(--row-border)'
    }`,
    // No lift and no translate — nothing shifts under the cursor mid-scan.
    transition: 'background 0.12s, border-color 0.12s',
  }
}

// The selected row's leading bar, drawn inside the row's own left inset so it
// never displaces content (see its call site).
const rowMarkerStyle: CSSProperties = {
  position: 'absolute',
  left: 'calc(var(--row-pad-x) / 2 - var(--row-marker-w) / 2)',
  top: 'var(--row-pad-y)', bottom: 'var(--row-pad-y)',
  width: 'var(--row-marker-w)',
  borderRadius: 'var(--row-marker-w)',
  background: 'var(--row-border-selected)',
}

function rowCheckboxZoneStyle(visible: boolean): CSSProperties {
  return {
    flexShrink: 0, width: 16, alignSelf: 'flex-start', height: 'var(--row-term-slot)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    opacity: visible ? 1 : 0.4, transition: 'opacity 0.15s', cursor: 'pointer',
  }
}

function rowCheckboxStyle(checked: boolean, aimed: boolean): CSSProperties {
  return {
    width: 14, height: 14, borderRadius: 4,
    border: `${aimed ? 2 : 1.5}px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
    background: checked ? 'var(--accent-fill)' : 'transparent',
    transition: 'border-color 0.12s',
  }
}

// The index numeral — its own fixed column beside all three slots, aligned to
// the term slot's line. Accent while this row is the selected one (§12.4), and
// otherwise on the same tertiary rung as the counters and the fraction: a
// numeral at a rung of its own is on §12's do-not-reintroduce list.
function rowNumberStyle(selected: boolean): CSSProperties {
  return {
    flexShrink: 0, width: 'var(--row-lead-w)', textAlign: 'right',
    alignSelf: 'flex-start', lineHeight: 'var(--row-term-slot)',
    fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    color: selected ? 'var(--accent)' : 'var(--row-absence)',
  }
}

// The three slots. Fixed heights throughout: the row's height must not depend
// on which fields a card happens to carry, which is the whole skeleton.
const rowContentStyle: CSSProperties = {
  flex: '1 1 auto', minWidth: 0,
  display: 'flex', flexDirection: 'column', gap: 'var(--row-line-gap)',
}

const rowTermSlotStyle: CSSProperties = {
  height: 'var(--row-term-slot)', flex: '0 0 var(--row-term-slot)',
  display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0,
}

// The scan subject: the only thing on the row at this size, and the one the
// list floor reserves width for. It keeps a shrink factor on purpose: the two
// neighbours that do NOT yield — the "no translation" statement and the
// trailing zone — have to be paid for by something when the term alone fills
// the line, and without it they are pushed out past the content column over
// the mastery column (measured: 88px for the absence, 36 for an image glyph).
// It is never asked to shrink while a translation has width, because the
// translation below contributes nothing to the line's overflow.
const rowTermStyle: CSSProperties = {
  fontFamily: 'var(--font-content)', fontSize: 17, fontWeight: 700,
  color: 'var(--text-primary)', lineHeight: 'var(--row-term-slot)',
  flex: '0 1 auto', minWidth: 0,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}

function rowTranslationStyle(active: boolean): CSSProperties {
  return {
    fontFamily: 'var(--font-content)', fontSize: 13, fontWeight: 400,
    color: active ? 'var(--accent)' : 'var(--text-secondary)',
    lineHeight: 'var(--row-term-slot)',
    // "The translation yields first" (§12.5), as a rule and not a weighting.
    // Basis 0: the translation adds nothing to the line's overflow, so the
    // term never shrinks on its account. It then GROWS into whatever width the
    // term and the trailing zone leave, capped at its own text so its click
    // target is the text and the zone's auto margin still takes the slack.
    // This replaced shrink weights (term 1, translation 1000): flex shrinks in
    // proportion to weight × width, so the term still gave a few hundredths of
    // a pixel whenever the translation overflowed, and `text-overflow` draws
    // an ellipsis for any overflow at all — "comprehensi…" with room to spare.
    flex: '1 1 0px', maxWidth: 'max-content', minWidth: 0,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }
}

// The row's ONE elastic zone. `marginLeft: auto` takes the slack, so counters
// appearing or disappearing never move the term at the head of the line.
const rowTrailingZoneStyle: CSSProperties = {
  marginLeft: 'auto', flexShrink: 0,
  display: 'flex', alignItems: 'center', gap: 6,
  alignSelf: 'center',
}

// A repeat is a labelled type tag with its count — never a bare numeral.
const rowRepeatTagStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
  fontFamily: 'var(--font-ui)', fontSize: 9.5, fontWeight: 700,
  letterSpacing: '0.07em',
  color: 'var(--row-absence)',
}

const rowRepeatCountStyle: CSSProperties = {
  fontWeight: 600, letterSpacing: 0, fontVariantNumeric: 'tabular-nums',
}

const rowBodyStyle: CSSProperties = {
  height: 'var(--row-body-slot)', flex: '0 0 var(--row-body-slot)',
  display: 'flex', flexDirection: 'column', gap: 'var(--row-body-gap)',
  minWidth: 0,
}

// One line per slot, always, with an explicit minimum so a slot can never be
// silently shrunk below its declared line (§12.8).
const rowBodyLineStyle: CSSProperties = {
  height: 'var(--row-body-line)', minHeight: 'var(--row-body-line)',
  flex: '0 0 var(--row-body-line)',
  display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden',
}

function rowBodyTextStyle(active: boolean, italic: boolean): CSSProperties {
  return {
    fontFamily: 'var(--font-content)', fontSize: 13,
    lineHeight: 'var(--row-body-line)',
    fontStyle: italic ? 'italic' : 'normal',
    color: active ? 'var(--accent)' : 'var(--text-secondary)',
    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    cursor: 'pointer',
  }
}

// Absence is written into the slot the field would have occupied. The role has
// its own token (`--row-absence`, an alias of the tertiary rung) so it is
// never restyled as content: lower case, no italic, no eyebrow spacing, no
// frame, no dash (§13).
const rowAbsenceStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 'var(--row-absence-size)',
  color: 'var(--row-absence)', whiteSpace: 'nowrap', flexShrink: 0,
}

// The mastery column: a fixed width whether or not a fraction is shown, so it
// runs straight down the list. NEW sits over an empty scale.
const rowMasteryColStyle: CSSProperties = {
  flexShrink: 0, width: 'var(--row-mastery-w)',
  alignSelf: 'flex-start',
  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
}

const rowMasteryStatusStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600,
  fontVariantNumeric: 'tabular-nums', lineHeight: 'var(--row-term-slot)',
  color: 'var(--row-absence)',
}

const extraThumbStyle: CSSProperties = {
  width: 16, height: 16,
  borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
  objectFit: 'cover', display: 'block',
}

const extraCountStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 9.5, fontWeight: 600,
  fontVariantNumeric: 'tabular-nums', color: 'var(--row-absence)',
}

// The READING PANE's card numeral. It used to borrow the row's, which is now
// a function of the row's selected state and carries the row's own tokens —
// two different objects that happened to look alike. The pane's numeral is
// unchanged in appearance; it just has its own declaration now.
const paneCardNumberStyle: CSSProperties = {
  flexShrink: 0, minWidth: 22, textAlign: 'right',
  fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600,
  color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums',
  lineHeight: '20px',
}

const rowStudiedBadgeStyle: CSSProperties = {
  flexShrink: 0,
  background: 'var(--accent-soft)', color: 'var(--accent)',
  borderRadius: 'var(--radius-pill)', padding: '0 6px',
  fontFamily: 'var(--font-ui)', fontSize: 8.5, fontWeight: 700,
  letterSpacing: '0.07em', textTransform: 'uppercase',
}

const DeckDesktopPanels = forwardRef(DeckDesktopPanelsImpl)
export default DeckDesktopPanels
